// Component for the Listen view
// Added import
import { ProFeatureUpgradeModal } from "@/components/ProFeatureUpgradeModal";
import { ConfigurableDemoEntry } from "@/components/app/ConfigurableDemoEntry";
import { StartOnlyPracticeEntry } from "@/components/app/StartOnlyPracticeEntry";
import {
  type LanguageLevel,
  PRACTICE_START_LABEL,
  PracticeLevelSelector,
  getPracticeLevelStartLabel,
} from "@/components/fullscreen/PracticeLevelSelector";
import { ImpromptuExperience } from "@/components/impromptu/ImpromptuExperience";
import { JourneyChapterCompletionButton } from "@/components/journey/JourneyChapterCompletionButton";
import { SpanishJourneyOverviewCard } from "@/components/journey/SpanishJourneyOverviewCard";
import { SpanishJourneyPathModal } from "@/components/journey/SpanishJourneyPathModal";
import { SpanishJourneyPlannerModal } from "@/components/journey/SpanishJourneyPlannerModal";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import { UpgradeRecoveryCard } from "@/components/paywall/UpgradeRecoveryCard";
import { ECOLE_DEFAULT_VIDEO } from "@/hooks/useBackgroundVideo";
import { useClassEnrollment } from "@/hooks/useClassEnrollment";
import type { Conversation } from "@/hooks/useConversationHistory";
import { useSpanishJourneyPlan } from "@/hooks/useSpanishJourneyPlan";
import {
  ACQUISITION_DEMO_EVENTS,
  trackAcquisitionDemoEvent,
} from "@/lib/analytics/acquisition-demo-events";
import { trackEventWithAttribution } from "@/lib/analytics/posthog-helpers";
import { formatBrandLanguageLabel } from "@/lib/brands";
import type { ConversationStartChoice } from "@/lib/conversationContinuation";
import { getFreemiumDiscountCoupon } from "@/lib/freemium/usage-state";
import { isImpromptuClientEnabled, resolveImpromptuLanguageCode } from "@/lib/impromptu/config";
import { deriveStandardLearningMode } from "@/lib/learning-mode";
import { getUserFacingConnectionFailureMessage } from "@/lib/livekit-user-facing-messages";
import { createLogger } from "@/lib/logger";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import {
  buildSpanishJourneyCoachingInstructions,
  getSpanishJourneyPlanCompletedCount,
  getSpanishJourneyPlanRecommendation,
  isSpanishJourneyCode,
  shouldApplySpanishJourneyRecommendation,
} from "@/lib/spanishJourneyPlanner";
import { clearLastSessionBoards } from "@/lib/whiteboard/sessionBoardStore";
import { RoomContext } from "@livekit/components-react";
// Import icons
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { DataPacket_Kind, RoomEvent } from "livekit-client";
// Import Button
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Headphones,
  Lock,
  Map as MapIcon,
  Mic,
  MicOff,
  PenLine,
  Play,
  RotateCw,
  Settings,
  Target,
  Volume2,
  X,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FillInTheGapCanvas from "../components/FillInTheGapCanvas";
import { HowItWorksModal } from "../components/HowItWorksModal";
import SettingsModal from "../components/SettingsModal";
// Import language data
import TimeLimitPopup, { type TimeLimitPopupCloseAction } from "../components/TimeLimitPopup";
import TranscriptionView from "../components/TranscriptionView";
import VoiceAgent from "../components/VoiceAgent";
import { ConversationStartChoiceSelector } from "../components/fullscreen/ConversationStartChoiceSelector";
import { VoiceFieldMic } from "../components/fullscreen/VoiceFieldMic";
import { AudioTrackError } from "../components/transcription-view/AudioTrackError";
import { WhiteboardSessionRecap } from "../components/whiteboard/WhiteboardSessionRecap";
import languages from "../data/languages.json";
// Import connection state type
import { AgentConnectionState, type TimeLimitPopupContext } from "../hooks/useLiveKitAgent";
import type { AppShellLayout } from "../lib/appShellLayout";
import {
  type LanguageJourneyChapter,
  type LanguageJourneyProgress,
  getLanguageJourneyByCode,
  getLanguageJourneyChapter,
  getLanguageJourneyChapterDisplayDescription,
  getLanguageJourneyChapterDisplayFocus,
  getLanguageJourneyChapterGroupId,
  getLanguageJourneyChapterGroups,
  getLanguageJourneyCompletionPercent,
  getLanguageJourneyNextIncompleteChapter,
  getLanguageJourneyProgressIdentityScope,
  getLanguageJourneyProgressNamespace,
  isLanguageJourneyChapterUnlocked,
  loadAndSyncLanguageJourneyProgress,
  loadLanguageJourneyProgress,
  markLanguageJourneyChapterComplete,
  markLanguageJourneyChapterIncomplete,
  syncLanguageJourneyProgress,
} from "../lib/languageJourney";
import { type PreSessionVariant, type SessionAnalyticsSurface } from "../lib/sessionLaunchPresets";
import { type SessionMode } from "../lib/sessionRouting";
import { type PodcastProject } from "../models/podcast";
// Note: useUserPreferences is not directly used in this file for nativeLanguageCode anymore,
// as it's passed via props. It might still be used for other preferences if any.
// import { useUserPreferences } from "../hooks/useLocalStorage";
// Import VoiceAgent from components
import { Button } from "../ui/button";
// Added import
import TranscriptText from "./TranscriptText";
import {
  shouldBootstrapConnectingAvatar,
  shouldDisableSessionAvatar,
  shouldRenderTranscriptionSession,
} from "./listen-view-session-state";
import { PlaybackButtons, ProgressBar } from "./player/PlayerControlsShared";

const FLAG_LANGUAGE_OPTIONS = [
  { name: "English", code: "en-US", flagSrc: "/flags/us.svg" },
  { name: "Spanish", code: "es-ES", flagSrc: "/flags/es.svg" },
  { name: "French", code: "fr-FR", flagSrc: "/flags/fr.svg" },
  { name: "German", code: "de-DE", flagSrc: "/flags/de.svg" },
  { name: "Italian", code: "it-IT", flagSrc: "/flags/it.svg" },
  { name: "Portuguese", code: "pt-BR", flagSrc: "/flags/pt-br.svg" },
  { name: "Japanese", code: "ja-JP", flagSrc: "/flags/jp.svg" },
  { name: "Dutch", code: "nl-NL", flagSrc: "/flags/nl.svg" },
  { name: "Mandarin", code: "cmn-CN", flagSrc: "/flags/cn.svg" },
] as const;

type PracticeSetupStep = "language" | "manual";
type TeachingStyle = "neutral" | "cheeky" | "chic" | "spartan";

const JOURNEY_CEFR_TO_LANGUAGE_LEVEL: Record<string, LanguageLevel> = {
  A1: "absolute_beginner",
  A2: "beginner",
  B1: "intermediate",
  B2: "intermediate",
  C1: "advanced",
};

function getLanguageLevelForJourneyCefrLevel(cefrLevel: string | null | undefined): LanguageLevel {
  return cefrLevel ? JOURNEY_CEFR_TO_LANGUAGE_LEVEL[cefrLevel] || "beginner" : "beginner";
}

function getLanguageLevelForJourneyChapter(chapter: LanguageJourneyChapter): LanguageLevel {
  return getLanguageLevelForJourneyCefrLevel(chapter.cefrLevel);
}

interface ListenViewProps {
  brandVariant?: "default" | "ecole" | "liceo";
  brandName?: string;
  segments: any[]; // TODO: Use correct type TranscriptSegment[]
  currentTime: number;
  showTranslation: boolean;
  isPlaying: boolean;
  togglePlay: () => void;
  onSegmentClick: (time: number) => void;
  onExplainSegment: (segment: any) => void; // TODO: Use correct type TranscriptSegment
  getSegmentClasses: (segment: any, index: number) => string; // TODO: Use correct type TranscriptSegment
  isRecording: boolean;
  isConnected: boolean;
  room: any; // TODO: Use correct type Room
  disconnectFromLiveKit: (
    triggeredByTimer?: boolean,
    invalidateTokenCache?: boolean,
    source?: string
  ) => void;
  toggleRecording: (sessionMode?: SessionMode) => Promise<boolean>;
  formatRecordingTime: (seconds: number) => string;
  recordingTime: number;
  connectionElapsedSeconds: number;
  showTranscript: boolean; // Add showTranscript prop
  setShowTranscript: (show: boolean) => void; // Add setShowTranscript prop
  selectedLanguageCode: string; // Add selectedLanguageCode prop
  setSelectedLanguageCode: (code: string) => void; // Add setSelectedLanguageCode prop
  nativeLanguageCode: string; // Add nativeLanguageCode prop from parent
  setNativeLanguageCode: (code: string) => void; // Add setNativeLanguageCode prop from parent
  guidedInstructionLanguageCode: string;
  translationLanguageCode: string;
  visualTranslationLanguageCode?: string;
  onTranslationLanguageChange: (code: string) => void;
  journeyChapterId: string | null;
  setJourneyChapterId: (chapterId: string | null) => void;
  podcastDiscussionId: string | null;
  isPodcastDiscussionEnabled: boolean;
  setIsPodcastDiscussionEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectedLanguageLevel: LanguageLevel;
  setSelectedLanguageLevel: (level: LanguageLevel) => void;
  selectedVoice: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede" | "Leda" | "Orus" | "Zephyr";
  setSelectedVoice: (
    voice: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede" | "Leda" | "Orus" | "Zephyr"
  ) => void;
  selectedTeachingStyle: "neutral" | "cheeky" | "chic" | "spartan";
  setSelectedTeachingStyle: (style: "neutral" | "cheeky" | "chic" | "spartan") => void;
  nickname: string;
  setNickname: (nickname: string) => void;
  customInstructions: string;
  setCustomInstructions: (instructions: string) => void;
  selectedNoAvatar: boolean;
  setSelectedNoAvatar: (noAvatar: boolean) => void;
  isAvatarForcedOff?: boolean;
  showTimeLimitPopup: boolean; // Add prop for popup visibility
  dismissTimeLimitPopup: () => void; // Add prop for dismissing popup
  timeLimitPopupContext?: TimeLimitPopupContext;
  hasExceededGuestLimit: boolean; // Add prop for guest limit status
  connectionState?: AgentConnectionState; // Add connection state with proper type
  connectionError?: string | null; // Add connection error
  retryConnection?: () => void; // Add retry connection function
  audioTrackError?: string | null; // Add audio track error
  audioRetryCount?: number; // Add audio retry count
  retryAudioTrack?: () => void; // Add retry audio track function
  clearAudioErrors?: () => void; // Add clear audio errors function
  sessionMode: SessionMode | null; // Add sessionMode prop
  onSessionModeChange?: (mode: SessionMode | null) => void; // Add callback for session mode changes
  isAuthenticated: boolean;
  userId: string | null;
  examplesBeforePractice?: boolean;
  onExamplesBeforePracticeChange?: (enabled: boolean) => void;
  tutorWhiteboardEnabled: boolean;
  onTutorWhiteboardEnabledChange: (enabled: boolean) => void;
  onCanvasVisibilityChange?: (isVisible: boolean) => void; // Add callback for canvas visibility changes
  startWritingPractice?: (fromLanguageName?: string, forceNew?: boolean) => void; // NEW: callback to trigger writing practice with native language
  subscribed: boolean; // Add subscribed prop
  userEmail?: string | null; // Optional: for RevenueCat checkout deep link
  onConversationActivity?: () => void; // Callback for streak updates
  currentStreak?: number | null;
  // Kept as hidden plumbing for future model A/B tests. The public selector now uses learning modes.
  selectedModel?: "pro" | "stealth";
  setSelectedModel?: (model: "pro" | "stealth") => void;
  hasSpatiusAvatarSession?: boolean;
  freemiumPreSessionOfferDismissSignal?: number;
  initialPracticeSetupStep?: PracticeSetupStep;
  preSessionVariant?: PreSessionVariant;
  analyticsSurface?: SessionAnalyticsSurface;
  embedAnalyticsEventPrefix?: string;
  onCommitLanguagePreferences?: () => void;
  onJourneyCoachingInstructionsChange?: (instructions: string | null) => void;
  shellLayout?: AppShellLayout;
  conversationStartChoice: ConversationStartChoice;
  setConversationStartChoice: (choice: ConversationStartChoice) => void;
  continuationConversation?: Conversation | null;
  previousTutorMessages?: string[];
  onPracticeSetupStepChange?: (step: PracticeSetupStep) => void;
}

const ListenView: React.FC<ListenViewProps> = ({
  brandVariant = "default",
  brandName,
  segments,
  currentTime,
  showTranslation,
  isPlaying,
  togglePlay,
  onSegmentClick,
  onExplainSegment,
  getSegmentClasses,
  isRecording,
  isConnected,
  room,
  disconnectFromLiveKit,
  toggleRecording,
  formatRecordingTime,
  recordingTime,
  connectionElapsedSeconds,
  showTranscript, // Receive showTranscript prop
  setShowTranscript, // Receive setShowTranscript prop
  selectedLanguageCode, // Receive selectedLanguageCode
  setSelectedLanguageCode, // Receive setSelectedLanguageCode prop
  nativeLanguageCode, // Receive nativeLanguageCode prop
  setNativeLanguageCode, // Receive setNativeLanguageCode prop
  guidedInstructionLanguageCode,
  translationLanguageCode,
  visualTranslationLanguageCode = translationLanguageCode,
  onTranslationLanguageChange,
  journeyChapterId,
  setJourneyChapterId,
  podcastDiscussionId,
  isPodcastDiscussionEnabled,
  setIsPodcastDiscussionEnabled,
  selectedLanguageLevel,
  setSelectedLanguageLevel,
  selectedVoice,
  setSelectedVoice,
  selectedTeachingStyle,
  setSelectedTeachingStyle,
  nickname,
  setNickname,
  customInstructions,
  setCustomInstructions,
  selectedNoAvatar,
  setSelectedNoAvatar,
  isAvatarForcedOff = false,
  showTimeLimitPopup, // Receive popup visibility prop
  dismissTimeLimitPopup, // Receive popup dismiss prop
  timeLimitPopupContext,
  hasExceededGuestLimit, // Receive guest limit status
  connectionState = "disconnected", // Default to disconnected
  connectionError = null,
  retryConnection,
  audioTrackError = null,
  audioRetryCount = 0,
  retryAudioTrack,
  clearAudioErrors,
  sessionMode, // Receive sessionMode prop
  onSessionModeChange, // Receive session mode change callback
  isAuthenticated,
  userId,
  tutorWhiteboardEnabled,
  onTutorWhiteboardEnabledChange,
  examplesBeforePractice = true,
  onExamplesBeforePracticeChange,
  onCanvasVisibilityChange, // Receive canvas visibility change callback
  startWritingPractice, // NEW prop
  subscribed = false, //NEW: default
  userEmail,
  onConversationActivity,
  currentStreak,
  hasSpatiusAvatarSession = false,
  freemiumPreSessionOfferDismissSignal = 0,
  initialPracticeSetupStep,
  preSessionVariant = "default",
  analyticsSurface = "fullscreen",
  embedAnalyticsEventPrefix,
  onCommitLanguagePreferences,
  onJourneyCoachingInstructionsChange,
  shellLayout = "fullscreen",
  conversationStartChoice,
  setConversationStartChoice,
  continuationConversation = null,
  previousTutorMessages = [],
  onPracticeSetupStepChange,
}) => {
  const logger = createLogger("ListenView");
  const posthog = usePostHog();
  const shouldReduceMotion = useReducedMotion();
  const [isImpromptuOpen, setIsImpromptuOpen] = useState(false);
  const [showSecondaryPractice, setShowSecondaryPractice] = useState(false);
  const selectedImpromptuLanguageCode = useMemo(
    () => resolveImpromptuLanguageCode(selectedLanguageCode),
    [selectedLanguageCode]
  );
  const showImpromptuEntry = isImpromptuClientEnabled() && selectedImpromptuLanguageCode !== null;
  const configuredAvatarProvider = (process.env.NEXT_PUBLIC_AVATAR_PROVIDER || "anam")
    .trim()
    .toLowerCase();
  const isSchool = brandVariant !== "default";
  const accentTextClass = isSchool ? "text-school-accent" : "text-orange-400";
  const accentTextSoftClass = isSchool ? "text-school-soft" : "text-orange-200";
  const accentBorderClass = isSchool ? "border-school-blue/40" : "border-orange-500/40";
  const accentBorderHoverClass = isSchool
    ? "hover:border-school-accent/50"
    : "hover:border-orange-500/50";
  const accentPrimaryButtonClass =
    brandVariant === "liceo"
      ? "bg-[#e12c26] text-white shadow-md shadow-black/20"
      : isSchool
        ? "bg-gradient-to-r from-school-blue to-school-accent text-white shadow-[0_0_24px_rgba(34,133,192,0.25)]"
        : "bg-orange-500 !text-[#171716] shadow-md shadow-orange-500/20";
  const accentPrimaryButtonHoverClass =
    brandVariant === "liceo"
      ? "hover:bg-[#c9231e]"
      : isSchool
        ? "hover:from-school-blue-hover hover:to-school-accent-hover"
        : "hover:bg-orange-600";
  const setupInlineControlBaseClass = `group inline-flex h-8 items-center gap-1 rounded-md px-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 ${
    isSchool
      ? "hover:bg-school-blue/20 focus-visible:ring-school-accent/50"
      : "hover:bg-white/[0.06] focus-visible:ring-orange-300/60"
  }`;
  const targetSetupHeaderControlClass = `group inline-flex h-auto items-center gap-2 rounded-md px-0.5 text-2xl font-black tracking-tight transition-colors hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 sm:text-3xl md:text-4xl ${
    isSchool
      ? "text-school-soft focus-visible:ring-school-accent/50"
      : "text-orange-300 focus-visible:ring-orange-300/50"
  }`;
  const explanationSetupInlineControlClass = `${setupInlineControlBaseClass} text-white/80 hover:text-white`;

  // nativeLanguageCode and setNativeLanguageCode are now received as props.
  // The local useUserPreferences call for these has been removed.

  // const [showLanguageDropdown, setShowLanguageDropdown] = useState(false); // No longer needed
  const [languageSearchTerm, setLanguageSearchTerm] = useState("");
  // Add refs to store the previous language codes, level, voice, and teaching style to detect actual changes
  const prevSelectedLanguageCodeRef = useRef<string | undefined>(selectedLanguageCode);
  const prevNativeLanguageCodeRef = useRef<string | undefined>(nativeLanguageCode); // This will now use the prop
  const prevSelectedLanguageLevelRef = useRef<string | undefined>(selectedLanguageLevel);
  const prevSelectedVoiceRef = useRef<string | undefined>(selectedVoice);
  const prevSelectedTeachingStyleRef = useRef<string | undefined>(selectedTeachingStyle);
  const prevNicknameRef = useRef<string | undefined>(nickname);
  const prevCustomInstructionsRef = useRef<string | undefined>(customInstructions);
  const lastAppliedJourneyLevelChapterRef = useRef<string | null>(null);
  const explicitlySelectedJourneyChapterRef = useRef<string | null>(null);
  const journeySyncContextRef = useRef({ userId, languageCode: selectedLanguageCode });
  journeySyncContextRef.current = { userId, languageCode: selectedLanguageCode };
  const practiceSetupLanguageSelectionCountRef = useRef(0);
  const practiceSetupLevelSelectionCountRef = useRef(0);
  const [animateLanguageShake, setAnimateLanguageShake] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isResumeUpgradeOpen, setIsResumeUpgradeOpen] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isTranscriptCollapsed, setIsTranscriptCollapsed] = useState(true);
  const [isConversationStartPending, setIsConversationStartPending] = useState(false);
  const [isJourneyMapOpen, setIsJourneyMapOpen] = useState(false);
  const [practiceSetupStep, setPracticeSetupStep] = useState<PracticeSetupStep>(
    initialPracticeSetupStep || "language"
  );
  const hasAppliedInitialPracticeSetupStepRef = useRef(false);
  const [canUseDocumentPortal, setCanUseDocumentPortal] = useState(false);
  // Active class (for mobile badge)
  const { selectedClassId, enrolledClasses } = useClassEnrollment();
  const activeClass = useMemo(
    () => enrolledClasses.find((c) => c.class_id === selectedClassId) || null,
    [enrolledClasses, selectedClassId]
  );

  // New state to indicate a session start is requested
  const [startSessionRequested, setStartSessionRequested] = useState<boolean>(false);

  const [fromLanguageSearchTerm, setFromLanguageSearchTerm] = useState(""); // Keep local search term
  const [isFromLanguageDropdownOpen, setIsFromLanguageDropdownOpen] = useState(false); // Keep local state for dropdown visibility

  // Microphone mute state
  const [isMuted, setIsMuted] = useState<boolean>(false);

  const setMicrophoneMuted = React.useCallback(
    async (muted: boolean) => {
      if (!room || !isConnected) return;

      try {
        await room.localParticipant.setMicrophoneEnabled(!muted);
        setIsMuted(muted);
        logger.info(`Microphone ${muted ? "muted" : "unmuted"}`);
      } catch (error) {
        logger.error("Failed to set microphone mute state:", error);
        throw error;
      }
    },
    [room, isConnected, logger]
  );

  // Toggle microphone mute function
  const toggleMute = React.useCallback(async () => {
    await setMicrophoneMuted(!isMuted);
  }, [isMuted, setMicrophoneMuted]);

  const voiceNoteTurnsEnabled =
    process.env.NEXT_PUBLIC_VOICE_NOTE_TURNS_ENABLED !== "false" && sessionMode !== "zen";

  // Reset mute state when session starts/ends
  React.useEffect(() => {
    if (!isRecording) {
      setIsMuted(false);
    }
  }, [isRecording]);

  // Derive the display name for the selected native language from props
  const fromLanguageName = useMemo(() => {
    return formatBrandLanguageLabel(
      Object.entries(languages).find(([, c]) => c === nativeLanguageCode)?.[0] || "",
      brandVariant
    );
  }, [nativeLanguageCode, brandVariant]);
  const guidedInstructionLanguageName = useMemo(() => {
    return formatBrandLanguageLabel(
      Object.entries(languages).find(([, c]) => c === guidedInstructionLanguageCode)?.[0] ||
        "English",
      brandVariant
    );
  }, [guidedInstructionLanguageCode, brandVariant]);
  const compactGuidedInstructionLanguageName = guidedInstructionLanguageName.replace(
    /\s*\([^)]*\)\s*$/,
    ""
  );
  const isConversationLearningMode = Boolean(
    selectedLanguageCode &&
    deriveStandardLearningMode(selectedLanguageCode, nativeLanguageCode) === "conversation"
  );
  const isJourneyLearningMode = sessionMode === "journey";
  const isPodcastDiscussionActive = Boolean(podcastDiscussionId && isPodcastDiscussionEnabled);
  const isEffectiveJourneyLearningMode = isJourneyLearningMode || isPodcastDiscussionActive;
  const [activeSessionLearningMode, setActiveSessionLearningMode] = useState<
    "guided" | "conversation" | "journey" | null
  >(null);
  const [transcriptTranslationsEnabled, setTranscriptTranslationsEnabled] = useState(false);

  useEffect(() => {
    if (!isRecording) setTranscriptTranslationsEnabled(false);
  }, [isRecording]);

  useEffect(() => {
    explicitlySelectedJourneyChapterRef.current = null;
  }, [selectedLanguageCode]);

  useEffect(() => {
    if (!isRecording) {
      setActiveSessionLearningMode(null);
      return;
    }

    setActiveSessionLearningMode(
      (current) =>
        current ??
        (isEffectiveJourneyLearningMode
          ? "journey"
          : isConversationLearningMode
            ? "conversation"
            : "guided")
    );
  }, [isConversationLearningMode, isEffectiveJourneyLearningMode, isRecording]);

  const handleActiveLearningModeChange = useCallback((mode: "conversation") => {
    setActiveSessionLearningMode(mode);
  }, []);
  const effectiveActiveSessionLearningMode =
    activeSessionLearningMode ??
    (isEffectiveJourneyLearningMode
      ? "journey"
      : isConversationLearningMode
        ? "conversation"
        : "guided");

  // All languages are now always available
  const isPracticeOnlyLanguage = false;

  // All languages are now always available
  const isPremiumLanguage = false;

  // Standard order classes for all learning modes
  const conversationOrder = "order-1 md:order-1";
  const visualOrder = "order-2 md:order-2";
  const writingOrder = "order-3 md:order-3";

  // Memoised helpers for selected language name & English check
  const selectedLanguageName = useMemo(() => {
    return formatBrandLanguageLabel(
      Object.entries(languages).find(([, c]) => c === selectedLanguageCode)?.[0] || "",
      brandVariant
    );
  }, [selectedLanguageCode, brandVariant]);
  const compactFromLanguageName = (fromLanguageName || "English").split(" (")[0];
  const instructionLanguagePicker = (
    <ResponsiveLanguagePicker
      open={isFromLanguageDropdownOpen}
      onOpenChange={setIsFromLanguageDropdownOpen}
      trigger={
        <button
          type="button"
          className={`${explanationSetupInlineControlClass} max-w-full`}
          aria-label={`Change explanation language, currently ${fromLanguageName || "English"}`}
        >
          <span className="min-w-0 truncate">{compactFromLanguageName}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/45" aria-hidden="true" />
        </button>
      }
      languages={languages}
      selectedLanguageCode={nativeLanguageCode}
      onSelectLanguage={setNativeLanguageCode}
      searchTerm={fromLanguageSearchTerm}
      onSearchTermChange={setFromLanguageSearchTerm}
      title="Choose an instruction language"
      brandVariant={brandVariant}
    />
  );
  const renderTargetLanguageControl = (controlClassName: string) => (
    <ResponsiveLanguagePicker
      open={isLanguageDropdownOpen}
      onOpenChange={setIsLanguageDropdownOpen}
      trigger={
        <button
          type="button"
          className={controlClassName}
          aria-label={`Change practice language, currently ${selectedLanguageName || "not selected"}`}
        >
          <span>{selectedLanguageName || "Choose language"}</span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-white/45 sm:h-5 sm:w-5"
            aria-hidden="true"
          />
        </button>
      }
      languages={languages}
      selectedLanguageCode={selectedLanguageCode}
      onSelectLanguage={setSelectedLanguageCode}
      searchTerm={languageSearchTerm}
      onSearchTermChange={setLanguageSearchTerm}
      title="Choose a practice language"
      brandVariant={brandVariant}
    />
  );
  const targetLanguageHeaderControl = renderTargetLanguageControl(targetSetupHeaderControlClass);
  const selectedJourney = useMemo(
    () => getLanguageJourneyByCode(selectedLanguageCode),
    [selectedLanguageCode]
  );
  const isSpanishJourney = isSpanishJourneyCode(selectedLanguageCode);
  const [isSpanishJourneyPlannerOpen, setIsSpanishJourneyPlannerOpen] = useState(false);
  const [spanishJourneyPlannerSource, setSpanishJourneyPlannerSource] = useState<
    "main_card" | "path"
  >("main_card");
  const {
    plan: spanishJourneyPlan,
    generating: isSpanishJourneyPlanGenerating,
    saving: isSpanishJourneyPlanSaving,
    error: spanishJourneyPlanError,
    generatePlan: generateSpanishJourneyPlan,
    savePlan: saveSpanishJourneyPlan,
  } = useSpanishJourneyPlan({
    enabled: isSpanishJourney,
    userId,
  });
  const journeyChapterGroups = useMemo(
    () => getLanguageJourneyChapterGroups(selectedJourney),
    [selectedJourney]
  );
  const selectedJourneyChapter = useMemo(
    () => getLanguageJourneyChapter(selectedLanguageCode, journeyChapterId),
    [journeyChapterId, selectedLanguageCode]
  );
  const [selectedJourneyLevel, setSelectedJourneyLevel] = useState<string>("A1");
  const [journeyProgress, setJourneyProgress] = useState<LanguageJourneyProgress>(() =>
    loadLanguageJourneyProgress(selectedLanguageCode, userId)
  );
  const completedJourneyChapterIds = useMemo(
    () => new Set(journeyProgress.completedChapterIds),
    [journeyProgress.completedChapterIds]
  );
  const selectedJourneyChapterCompleted = selectedJourneyChapter
    ? completedJourneyChapterIds.has(selectedJourneyChapter.id)
    : false;
  const journeyCompletionPercent = useMemo(
    () => getLanguageJourneyCompletionPercent(selectedJourney, journeyProgress),
    [journeyProgress, selectedJourney]
  );
  const genericJourneyRecommendation = useMemo(
    () =>
      isSpanishJourney
        ? null
        : getLanguageJourneyNextIncompleteChapter(selectedJourney, journeyProgress),
    [isSpanishJourney, journeyProgress, selectedJourney]
  );
  const spanishJourneyRecommendation = useMemo(
    () => getSpanishJourneyPlanRecommendation(spanishJourneyPlan, journeyProgress),
    [journeyProgress, spanishJourneyPlan]
  );
  const spanishJourneyPlanCompletedCount = useMemo(
    () =>
      spanishJourneyPlan
        ? getSpanishJourneyPlanCompletedCount(spanishJourneyPlan, journeyProgress)
        : 0,
    [journeyProgress, spanishJourneyPlan]
  );
  const spanishJourneyCoachingInstructions = useMemo(
    () => buildSpanishJourneyCoachingInstructions(spanishJourneyPlan, journeyChapterId),
    [journeyChapterId, spanishJourneyPlan]
  );
  const selectedJourneyLevelGroup =
    journeyChapterGroups.find((group) => group.id === selectedJourneyLevel) ||
    journeyChapterGroups[0] ||
    null;
  const [audioReadyJourneyPodcastChapterIds, setAudioReadyJourneyPodcastChapterIds] = useState<
    Set<string>
  >(new Set());
  const [podcastDiscussionProject, setPodcastDiscussionProject] = useState<PodcastProject | null>(
    null
  );
  const [podcastDiscussionLoadFailed, setPodcastDiscussionLoadFailed] = useState(false);
  const selectedJourneyChapterUnlocked = isLanguageJourneyChapterUnlocked(
    selectedJourneyChapter,
    subscribed
  );
  const selectedLanguageLevelStartLabel = getPracticeLevelStartLabel(selectedLanguageLevel);
  const hasSavedPracticeSetup = Boolean(userEmail && selectedLanguageCode);
  const shouldShowManualStartFallback = hasSavedPracticeSetup || practiceSetupStep === "manual";
  const isUnauthenticatedFirstUse = !userEmail && !isSchool;
  const effectivePracticeSetupStep: PracticeSetupStep = shouldShowManualStartFallback
    ? "manual"
    : practiceSetupStep;

  useEffect(() => {
    setCanUseDocumentPortal(true);
  }, []);

  useEffect(() => {
    onPracticeSetupStepChange?.(effectivePracticeSetupStep);
  }, [effectivePracticeSetupStep, onPracticeSetupStepChange]);

  useEffect(() => {
    if (!initialPracticeSetupStep || hasAppliedInitialPracticeSetupStepRef.current) return;
    setPracticeSetupStep(initialPracticeSetupStep);
    hasAppliedInitialPracticeSetupStepRef.current = true;
  }, [initialPracticeSetupStep]);

  useEffect(() => {
    let cancelled = false;
    const identityId = userId;
    const languageCode = selectedLanguageCode;
    setJourneyProgress(loadLanguageJourneyProgress(languageCode, identityId));
    void loadAndSyncLanguageJourneyProgress(languageCode, identityId).then((progress) => {
      if (!cancelled) setJourneyProgress(progress);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedLanguageCode, userId]);

  useEffect(() => {
    onJourneyCoachingInstructionsChange?.(
      isSpanishJourney && spanishJourneyCoachingInstructions
        ? spanishJourneyCoachingInstructions
        : null
    );
  }, [isSpanishJourney, onJourneyCoachingInstructionsChange, spanishJourneyCoachingInstructions]);

  useEffect(
    () => () => {
      onJourneyCoachingInstructionsChange?.(null);
    },
    [onJourneyCoachingInstructionsChange]
  );

  useEffect(() => {
    if (
      !isSpanishJourney ||
      spanishJourneyPlan?.status !== "active" ||
      !spanishJourneyRecommendation ||
      !selectedJourney ||
      isRecording
    ) {
      return;
    }

    const defaultChapterId = selectedJourney.chapters[0]?.id;
    const shouldUseRecommendation = shouldApplySpanishJourneyRecommendation({
      selectedChapterId: journeyChapterId,
      defaultChapterId: defaultChapterId || null,
      explicitlySelectedChapterId: explicitlySelectedJourneyChapterRef.current,
    });
    if (!shouldUseRecommendation || journeyChapterId === spanishJourneyRecommendation.id) return;

    setJourneyChapterId(spanishJourneyRecommendation.id);
    setSelectedLanguageLevel(getLanguageLevelForJourneyChapter(spanishJourneyRecommendation));
    setSelectedJourneyLevel(
      getLanguageJourneyChapterGroupId(selectedJourney, spanishJourneyRecommendation) ||
        spanishJourneyRecommendation.cefrLevel
    );
  }, [
    completedJourneyChapterIds,
    isRecording,
    isSpanishJourney,
    journeyChapterId,
    selectedJourney,
    setJourneyChapterId,
    setSelectedLanguageLevel,
    spanishJourneyPlan?.status,
    spanishJourneyRecommendation,
  ]);

  useEffect(() => {
    if (
      isSpanishJourney ||
      !genericJourneyRecommendation ||
      !selectedJourney ||
      !isLanguageJourneyChapterUnlocked(genericJourneyRecommendation, subscribed) ||
      isRecording
    ) {
      return;
    }

    const selectedIsExplicit =
      Boolean(journeyChapterId) && explicitlySelectedJourneyChapterRef.current === journeyChapterId;
    const selectedIsDefault =
      !journeyChapterId || journeyChapterId === selectedJourney.chapters[0]?.id;
    const selectedIsComplete = Boolean(
      journeyChapterId && completedJourneyChapterIds.has(journeyChapterId)
    );
    if (
      selectedIsExplicit ||
      (!selectedIsDefault && !selectedIsComplete) ||
      journeyChapterId === genericJourneyRecommendation.id
    ) {
      return;
    }

    setJourneyChapterId(genericJourneyRecommendation.id);
    setSelectedLanguageLevel(getLanguageLevelForJourneyChapter(genericJourneyRecommendation));
    setSelectedJourneyLevel(
      getLanguageJourneyChapterGroupId(selectedJourney, genericJourneyRecommendation) ||
        genericJourneyRecommendation.cefrLevel
    );
  }, [
    completedJourneyChapterIds,
    genericJourneyRecommendation,
    isRecording,
    isSpanishJourney,
    journeyChapterId,
    selectedJourney,
    setJourneyChapterId,
    setSelectedLanguageLevel,
    subscribed,
  ]);

  useEffect(() => {
    if (!selectedJourney) {
      setAudioReadyJourneyPodcastChapterIds(new Set());
      return;
    }

    let isCancelled = false;
    const loadChapterPodcastAvailability = async () => {
      try {
        const params = new URLSearchParams({
          scope: "public",
          language: selectedJourney.languageName,
        });
        const response = await fetch(`/api/podcasts?${params}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to load podcast availability");

        const audioReadyChapters = new Set(
          ((data.podcasts || []) as PodcastProject[])
            .filter(
              (podcast) =>
                podcast.source === "chapter" &&
                podcast.status === "audio_ready" &&
                podcast.visibility === "public" &&
                podcast.chapterLanguageCode === selectedJourney.languageCode &&
                podcast.chapterId &&
                podcast.segments.some((segment) => segment.audioUrl)
            )
            .map((podcast) => podcast.chapterId!)
        );

        if (!isCancelled) setAudioReadyJourneyPodcastChapterIds(audioReadyChapters);
      } catch {
        if (!isCancelled) setAudioReadyJourneyPodcastChapterIds(new Set());
      }
    };

    loadChapterPodcastAvailability();

    return () => {
      isCancelled = true;
    };
  }, [selectedJourney]);

  useEffect(() => {
    if (!podcastDiscussionId) {
      setPodcastDiscussionProject(null);
      setPodcastDiscussionLoadFailed(false);
      return;
    }

    let isCancelled = false;

    const loadPodcastDiscussionProject = async () => {
      setPodcastDiscussionLoadFailed(false);

      try {
        const response = await fetch(`/api/podcasts/${encodeURIComponent(podcastDiscussionId)}`);
        const contentType = response.headers.get("content-type") || "";
        const data = contentType.includes("application/json") ? await response.json() : null;

        if (!response.ok) {
          throw new Error(data?.error || "Failed to load podcast discussion context");
        }

        if (!isCancelled) {
          setPodcastDiscussionProject((data?.podcast as PodcastProject) || null);
        }
      } catch {
        if (!isCancelled) {
          setPodcastDiscussionProject(null);
          setPodcastDiscussionLoadFailed(true);
        }
      }
    };

    loadPodcastDiscussionProject();

    return () => {
      isCancelled = true;
    };
  }, [podcastDiscussionId]);

  useEffect(() => {
    const handleProgressUpdate = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          languageCode?: string;
          progressNamespace?: string;
          identityScope?: string;
        }>
      ).detail;
      const selectedProgressNamespace = getLanguageJourneyProgressNamespace(selectedLanguageCode);
      if (detail?.progressNamespace && detail.progressNamespace !== selectedProgressNamespace) {
        return;
      }
      if (
        !detail?.progressNamespace &&
        detail?.languageCode &&
        detail.languageCode !== selectedLanguageCode
      ) {
        return;
      }
      if (
        detail?.identityScope &&
        detail.identityScope !== getLanguageJourneyProgressIdentityScope(userId)
      ) {
        return;
      }
      setJourneyProgress(loadLanguageJourneyProgress(selectedLanguageCode, userId));
    };

    window.addEventListener("language-journey-progress-updated", handleProgressUpdate);
    return () => {
      window.removeEventListener("language-journey-progress-updated", handleProgressUpdate);
    };
  }, [selectedLanguageCode, userId]);

  useEffect(() => {
    if (!isEffectiveJourneyLearningMode) {
      lastAppliedJourneyLevelChapterRef.current = null;
    }

    const selectedJourneyGroupId = getLanguageJourneyChapterGroupId(
      selectedJourney,
      selectedJourneyChapter
    );
    const selectedJourneyCefrLevel = selectedJourneyChapter?.cefrLevel;
    if (selectedJourneyGroupId) {
      setSelectedJourneyLevel(selectedJourneyGroupId);
    }

    if (selectedJourneyCefrLevel) {
      if (
        isEffectiveJourneyLearningMode &&
        selectedJourneyChapter.id !== lastAppliedJourneyLevelChapterRef.current
      ) {
        lastAppliedJourneyLevelChapterRef.current = selectedJourneyChapter.id;
        const journeyLanguageLevel = getLanguageLevelForJourneyCefrLevel(selectedJourneyCefrLevel);
        if (selectedLanguageLevel !== journeyLanguageLevel) {
          setSelectedLanguageLevel(journeyLanguageLevel);
        }
      }
      return;
    }

    const firstLevel = journeyChapterGroups[0]?.id;
    if (firstLevel) setSelectedJourneyLevel(firstLevel);
  }, [
    selectedJourney,
    isEffectiveJourneyLearningMode,
    journeyChapterGroups,
    selectedJourneyChapter?.cefrLevel,
    selectedJourneyChapter?.id,
    selectedJourneyChapter,
    selectedLanguageLevel,
    setSelectedLanguageLevel,
  ]);

  useEffect(() => {
    if (!isEffectiveJourneyLearningMode) {
      setIsJourneyMapOpen(false);
    }
  }, [isEffectiveJourneyLearningMode]);

  useEffect(() => {
    if (!isJourneyMapOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsJourneyMapOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isJourneyMapOpen]);

  useEffect(() => {
    if (!isEffectiveJourneyLearningMode || !selectedJourney) return;
    if (
      !selectedJourneyChapter ||
      isLanguageJourneyChapterUnlocked(selectedJourneyChapter, subscribed)
    ) {
      return;
    }

    const fallbackChapter =
      selectedJourney.chapters.find((chapter) =>
        isLanguageJourneyChapterUnlocked(chapter, subscribed)
      ) ||
      selectedJourney.chapters[0] ||
      null;
    setJourneyChapterId(fallbackChapter?.id || null);
  }, [
    isEffectiveJourneyLearningMode,
    selectedJourney,
    selectedJourneyChapter,
    setJourneyChapterId,
    subscribed,
  ]);

  // Effect to start the session when sessionMode is set and a start is requested
  useEffect(() => {
    let startTimeout: ReturnType<typeof setTimeout> | null = null;

    if (sessionMode && startSessionRequested) {
      logger.info("Session start requested", { sessionMode, startSessionRequested });

      // First, ensure the session mode is propagated to the parent
      if (onSessionModeChange) {
        logger.info("Notifying parent of session mode change", { sessionMode });
        onSessionModeChange(sessionMode);
      }

      // Use a small delay to ensure the session mode has been propagated
      // to the parent component and the useLiveKitAgent hook
      startTimeout = setTimeout(() => {
        logger.info("Starting session after delay", {
          sessionMode,
          selectedLanguageCode,
          hasExceededGuestLimit,
        });

        // Check for guest limit before toggling recording
        if (!selectedLanguageCode && !hasExceededGuestLimit) {
          setAnimateLanguageShake(true);
          setIsLanguageDropdownOpen(true);
          setTimeout(() => setAnimateLanguageShake(false), 500);
        } else if (selectedLanguageCode && !hasExceededGuestLimit) {
          logger.info("Calling toggleRecording with session mode", { sessionMode });
          void toggleRecording(sessionMode || undefined);
        }
        // Reset the request state
        setStartSessionRequested(false);
      }, 50); // Small delay to ensure state propagation
    }

    return () => {
      if (startTimeout) {
        clearTimeout(startTimeout);
      }
    };
  }, [
    sessionMode,
    startSessionRequested,
    hasExceededGuestLimit,
    selectedLanguageCode,
    toggleRecording,
    onSessionModeChange,
  ]);

  useEffect(() => {
    if (isConnected || connectionState === "failed") {
      setIsConversationStartPending(false);
      return;
    }

    if (!startSessionRequested && !isRecording && connectionState === "disconnected") {
      setIsConversationStartPending(false);
    }
  }, [connectionState, isConnected, isRecording, startSessionRequested]);

  useEffect(() => {
    if (!showTimeLimitPopup) return;
    setIsConversationStartPending(false);
    setStartSessionRequested(false);
    setShowSessionLimitRecovery(false);
  }, [showTimeLimitPopup]);

  useEffect(() => {
    if (!freemiumPreSessionOfferDismissSignal) return;
    setIsConversationStartPending(false);
    setStartSessionRequested(false);
  }, [freemiumPreSessionOfferDismissSignal]);

  // Effect to handle language change (either learning or native) during an active session
  useEffect(() => {
    const learningLanguageChanged = selectedLanguageCode !== prevSelectedLanguageCodeRef.current;
    // nativeLanguageCode is now a prop
    const nativeLanguageChanged = nativeLanguageCode !== prevNativeLanguageCodeRef.current;

    if (
      (learningLanguageChanged || nativeLanguageChanged) &&
      isRecording &&
      sessionMode // Ensure a session mode is active (e.g., "conversation" or "visual")
    ) {
      logger.info("Language setting changed while session active. Reconnecting...", {
        oldLearningLang: prevSelectedLanguageCodeRef.current,
        newLearningLang: selectedLanguageCode,
        oldNativeLang: prevNativeLanguageCodeRef.current,
        newNativeLang: nativeLanguageCode, // This is now the prop value
        currentSessionMode: sessionMode,
      });

      // 1. Disconnect the current session.
      // This will also set isRecording to false internally via useLiveKitAgent.
      disconnectFromLiveKit(false, true, "language_change_restart");

      // 2. Request a new session start.
      // The existing useEffect in ListenView (that depends on [sessionMode, startSessionRequested, ...])
      // will detect this change. Since isRecording will be false after the disconnect,
      // toggleRecording() within that effect will initiate a new connection.
      // The useLiveKitAgent hook will use the new selectedLanguageCode and nativeLanguageCode props for the new connection.
      setStartSessionRequested(true);
    }

    // Update refs with current language codes for the next comparison
    prevSelectedLanguageCodeRef.current = selectedLanguageCode;
    prevNativeLanguageCodeRef.current = nativeLanguageCode; // This is now the prop value
  }, [
    selectedLanguageCode,
    nativeLanguageCode, // Add nativeLanguageCode (prop) to dependencies
    isRecording,
    sessionMode,
    disconnectFromLiveKit,
    setStartSessionRequested,
    logger,
  ]);

  // Effect to handle language level change during an active session
  useEffect(() => {
    const languageLevelChanged = selectedLanguageLevel !== prevSelectedLanguageLevelRef.current;

    if (languageLevelChanged && isRecording && sessionMode) {
      logger.info("Language level changed during active session, restarting...", {
        oldLevel: prevSelectedLanguageLevelRef.current,
        newLevel: selectedLanguageLevel,
        currentSessionMode: sessionMode,
      });

      // Disconnect and restart session with new level
      disconnectFromLiveKit(false, true, "language_level_change_restart");
      setStartSessionRequested(true);
    }

    // Update ref with current language level for the next comparison
    prevSelectedLanguageLevelRef.current = selectedLanguageLevel;
  }, [
    selectedLanguageLevel,
    isRecording,
    sessionMode,
    disconnectFromLiveKit,
    setStartSessionRequested,
    logger,
  ]);

  // Effect to handle voice change during an active session
  useEffect(() => {
    const voiceChanged = selectedVoice !== prevSelectedVoiceRef.current;

    if (voiceChanged && isRecording && sessionMode) {
      logger.info("Voice changed during active session, restarting...", {
        oldVoice: prevSelectedVoiceRef.current,
        newVoice: selectedVoice,
        currentSessionMode: sessionMode,
      });

      // Disconnect and restart session with new voice
      disconnectFromLiveKit(false, true, "voice_change_restart");
      setStartSessionRequested(true);
    }

    // Update ref with current voice for the next comparison
    prevSelectedVoiceRef.current = selectedVoice;
  }, [
    selectedVoice,
    isRecording,
    sessionMode,
    disconnectFromLiveKit,
    setStartSessionRequested,
    logger,
  ]);

  // Effect to handle teaching style change during an active session
  useEffect(() => {
    const teachingStyleChanged = selectedTeachingStyle !== prevSelectedTeachingStyleRef.current;

    if (teachingStyleChanged && isRecording && sessionMode) {
      logger.info("Teaching style changed during active session, restarting...", {
        oldStyle: prevSelectedTeachingStyleRef.current,
        newStyle: selectedTeachingStyle,
        currentSessionMode: sessionMode,
      });

      // Disconnect and restart session with new teaching style
      disconnectFromLiveKit(false, true, "teaching_style_change_restart");
      setStartSessionRequested(true);
    }

    // Update ref with current teaching style for the next comparison
    prevSelectedTeachingStyleRef.current = selectedTeachingStyle;
  }, [
    selectedTeachingStyle,
    isRecording,
    sessionMode,
    disconnectFromLiveKit,
    setStartSessionRequested,
    logger,
  ]);

  // Effect to handle personalization changes during an active session
  useEffect(() => {
    const nicknameChanged = nickname !== prevNicknameRef.current;
    const customInstructionsChanged = customInstructions !== prevCustomInstructionsRef.current;

    if ((nicknameChanged || customInstructionsChanged) && isRecording && sessionMode) {
      logger.info("Personalisation changed during active session, restarting...", {
        nicknameChanged,
        customInstructionsChanged,
        currentSessionMode: sessionMode,
      });

      disconnectFromLiveKit(false, true, "personalization_change_restart");
      setStartSessionRequested(true);
    }

    prevNicknameRef.current = nickname;
    prevCustomInstructionsRef.current = customInstructions;
  }, [
    nickname,
    customInstructions,
    isRecording,
    sessionMode,
    disconnectFromLiveKit,
    setStartSessionRequested,
    logger,
  ]);

  // State for canvas exercise
  const [showCanvas, setShowCanvas] = useState(false);
  const [showSessionLimitRecovery, setShowSessionLimitRecovery] = useState(false);
  const [canvasExerciseContent, setCanvasExerciseContent] = useState("");
  const [canvasExerciseOptions, setCanvasExerciseOptions] = useState<string[]>([]);
  const [pendingAnswerSubmission, setPendingAnswerSubmission] = useState<string | null>(null);
  const shouldBootstrapAvatar = shouldBootstrapConnectingAvatar({
    configuredAvatarProvider,
    connectionState,
    hasSpatiusAvatarSession,
    isConnected,
    isRecording,
    selectedNoAvatar,
  });
  const isStartSpeakingPending =
    isConversationStartPending ||
    startSessionRequested ||
    connectionState === "connecting" ||
    connectionState === "reconnecting";
  const shouldRenderSessionTranscript = shouldRenderTranscriptionSession({
    isConnected,
    isRecording,
  });
  const shouldDisableTranscriptAvatar = shouldDisableSessionAvatar({
    configuredAvatarProvider,
    hasSpatiusAvatarSession,
    selectedNoAvatar,
  });
  const shouldShowReadyScreen = !shouldRenderSessionTranscript && connectionState !== "failed";

  const wasRecordingRef = useRef(isRecording);
  useEffect(() => {
    if (!wasRecordingRef.current && isRecording) {
      clearLastSessionBoards();
    }
    wasRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    if (!shouldShowReadyScreen || shouldShowManualStartFallback) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [practiceSetupStep, shouldShowManualStartFallback, shouldShowReadyScreen]);

  useEffect(() => {
    if (!isPodcastDiscussionActive || !selectedJourney || !selectedLanguageCode) {
      return;
    }

    const defaultChapter = getLanguageJourneyChapter(selectedLanguageCode, journeyChapterId);
    if (!journeyChapterId && defaultChapter) {
      setJourneyChapterId(defaultChapter.id);
    }
    if (sessionMode !== "journey") {
      onSessionModeChange?.("journey");
    }
    if (nativeLanguageCode !== guidedInstructionLanguageCode) {
      setNativeLanguageCode(guidedInstructionLanguageCode);
    }
  }, [
    guidedInstructionLanguageCode,
    isPodcastDiscussionActive,
    journeyChapterId,
    nativeLanguageCode,
    onSessionModeChange,
    selectedJourney,
    selectedLanguageCode,
    sessionMode,
    setJourneyChapterId,
    setNativeLanguageCode,
  ]);

  const handleStartConversation = useCallback(async () => {
    if (!selectedLanguageCode && !hasExceededGuestLimit) {
      setAnimateLanguageShake(true);
      setIsLanguageDropdownOpen(true);
      setTimeout(() => setAnimateLanguageShake(false), 500);
      return;
    }

    setIsConversationStartPending(true);

    const startMode = isEffectiveJourneyLearningMode ? "journey" : "conversation";
    if (startMode === "journey" && !selectedJourneyChapterUnlocked) {
      setIsConversationStartPending(false);
      return;
    }

    if (onSessionModeChange) onSessionModeChange(startMode);
    const started = await toggleRecording(startMode);
    if (!started) {
      setIsConversationStartPending(false);
    }
  }, [
    hasExceededGuestLimit,
    isEffectiveJourneyLearningMode,
    onSessionModeChange,
    selectedJourneyChapterUnlocked,
    selectedLanguageCode,
    toggleRecording,
  ]);

  const handleSetupLanguageSelection = useCallback(
    (languageCode: string) => {
      practiceSetupLanguageSelectionCountRef.current += 1;
      const selectionCount = practiceSetupLanguageSelectionCountRef.current;
      if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
        posthog.capture("listenview_practice_setup_language_selected", {
          target_language_code: languageCode,
          target_language_name:
            Object.entries(languages).find(([, code]) => code === languageCode)?.[0] || null,
          previous_language_code: selectedLanguageCode || null,
          changed: selectedLanguageCode !== languageCode,
          is_reselection: selectionCount > 1,
          selection_count: selectionCount,
          has_exceeded_guest_limit: hasExceededGuestLimit,
          surface: "practice_setup",
        });
      }
      setAnimateLanguageShake(false);
      setIsLanguageDropdownOpen(false);
      setSelectedLanguageCode(languageCode);
      setPracticeSetupStep("manual");
    },
    [hasExceededGuestLimit, posthog, selectedLanguageCode, setSelectedLanguageCode]
  );

  const handleReadyLevelSelection = useCallback(
    (level: LanguageLevel) => {
      practiceSetupLevelSelectionCountRef.current += 1;
      const selectionCount = practiceSetupLevelSelectionCountRef.current;
      if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
        posthog.capture("listenview_practice_setup_level_selected", {
          language_level: level,
          previous_language_level: selectedLanguageLevel || null,
          target_language_code: selectedLanguageCode,
          target_language_name: selectedLanguageName,
          changed: selectedLanguageLevel !== level,
          is_reselection: selectionCount > 1,
          selection_count: selectionCount,
          has_exceeded_guest_limit: hasExceededGuestLimit,
          surface: "practice_setup",
        });
      }

      setSelectedLanguageLevel(level);
    },
    [
      hasExceededGuestLimit,
      posthog,
      selectedLanguageCode,
      selectedLanguageLevel,
      selectedLanguageName,
      setSelectedLanguageLevel,
    ]
  );

  const handleLearningModeChange = (mode: "guided" | "conversation" | "journey") => {
    if (!selectedLanguageCode) {
      return;
    }
    const previousLearningMode = isEffectiveJourneyLearningMode
      ? "journey"
      : isConversationLearningMode
        ? "conversation"
        : "guided";
    const journeyInstructionLanguageCode =
      selectedJourney?.instructionLanguageCode || guidedInstructionLanguageCode;
    const journeyInstructionLanguageName =
      Object.entries(languages).find(([, code]) => code === journeyInstructionLanguageCode)?.[0] ||
      selectedJourney?.instructionLanguage ||
      guidedInstructionLanguageName;
    const nextInstructionLanguageCode =
      mode === "conversation"
        ? selectedLanguageCode
        : mode === "journey"
          ? journeyInstructionLanguageCode
          : guidedInstructionLanguageCode;
    const nextInstructionLanguageName =
      mode === "conversation"
        ? selectedLanguageName
        : mode === "journey"
          ? journeyInstructionLanguageName
          : guidedInstructionLanguageName;

    if (previousLearningMode !== mode && posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.capture("listenview_learning_mode_changed", {
        learning_mode: mode,
        previous_learning_mode: previousLearningMode,
        target_language_code: selectedLanguageCode,
        target_language_name: selectedLanguageName,
        instruction_language_code: nextInstructionLanguageCode,
        instruction_language_name: nextInstructionLanguageName,
        surface: "fullscreen",
      });
    }

    if (mode === "conversation") {
      if (isPodcastDiscussionActive) setIsPodcastDiscussionEnabled(false);
      if (onSessionModeChange) onSessionModeChange(null);
      setNativeLanguageCode(selectedLanguageCode);
      return;
    }

    if (mode === "journey") {
      const defaultChapter = getLanguageJourneyChapter(selectedLanguageCode, journeyChapterId);
      setJourneyChapterId(defaultChapter?.id || null);
      if (onSessionModeChange) onSessionModeChange("journey");
      setNativeLanguageCode(journeyInstructionLanguageCode);
      setIsJourneyMapOpen(true);
      return;
    }

    if (isPodcastDiscussionActive) setIsPodcastDiscussionEnabled(false);
    if (onSessionModeChange) onSessionModeChange(null);
    setNativeLanguageCode(guidedInstructionLanguageCode);
  };

  const handleJourneyChapterSelect = (chapter: LanguageJourneyChapter) => {
    if (!isLanguageJourneyChapterUnlocked(chapter, subscribed)) {
      return;
    }

    explicitlySelectedJourneyChapterRef.current = chapter.id;
    setSelectedJourneyLevel(
      getLanguageJourneyChapterGroupId(selectedJourney, chapter) || chapter.cefrLevel
    );
    setSelectedLanguageLevel(getLanguageLevelForJourneyChapter(chapter));
    setJourneyChapterId(chapter.id);
  };

  const handleToggleJourneyChapterComplete = () => {
    if (!selectedLanguageCode || !selectedJourneyChapter) return;
    const languageCode = selectedLanguageCode;
    const identityId = userId;
    const nextProgress = selectedJourneyChapterCompleted
      ? markLanguageJourneyChapterIncomplete(languageCode, selectedJourneyChapter.id, identityId)
      : markLanguageJourneyChapterComplete(languageCode, selectedJourneyChapter.id, identityId);

    setJourneyProgress(nextProgress);
    if (identityId) {
      void syncLanguageJourneyProgress(languageCode, identityId)
        .then((synced) => {
          if (
            journeySyncContextRef.current.userId === identityId &&
            journeySyncContextRef.current.languageCode === languageCode
          ) {
            setJourneyProgress(synced);
          }
        })
        .catch(() => {
          logger.warn("Language Journey progress sync deferred; local progress retained");
        });
    }
  };

  const handleSaveSpanishJourneyPlan = async (
    plan: Parameters<typeof saveSpanishJourneyPlan>[0]
  ) => {
    const saved = await saveSpanishJourneyPlan(plan);
    const recommendation = getSpanishJourneyPlanRecommendation(saved, journeyProgress);
    if (recommendation) {
      handleJourneyChapterSelect(recommendation);
    }
    if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.capture("spanish_journey_plan_activated", {
        plan_version: saved.version,
        catalog_version: saved.catalogVersion,
        course_version: saved.courseVersion,
        session_count: saved.items.length,
        estimated_weeks: saved.estimatedWeeks,
        goal: saved.profile.goal,
        ability: saved.profile.ability,
        starting_chapter_id: saved.profile.startingChapterId || saved.items[0]?.chapterId || null,
        teaching_approach: saved.profile.teachingApproach,
        stuck_strategy: saved.profile.stuckStrategy,
        correction_timing: saved.profile.correctionTiming,
        language_support: saved.profile.languageSupport,
        challenge_level: saved.profile.challengeLevel,
        first_chapter_id: saved.items[0]?.chapterId || null,
      });
    }
  };

  const handleGenerateSpanishJourneyPlan = async (
    profile: Parameters<typeof generateSpanishJourneyPlan>[0]
  ) => {
    const generated = await generateSpanishJourneyPlan(profile);
    if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.capture("spanish_journey_plan_generated", {
        plan_version: generated.version,
        session_count: generated.items.length,
        goal: generated.profile.goal,
        ability: generated.profile.ability,
        starting_chapter_id:
          generated.profile.startingChapterId || generated.items[0]?.chapterId || null,
        teaching_approach: generated.profile.teachingApproach,
        stuck_strategy: generated.profile.stuckStrategy,
        time_available: generated.profile.timeAvailable,
      });
    }
    return generated;
  };

  const openSpanishJourneyPlanner = (source: "main_card" | "path") => {
    if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.capture("spanish_journey_planner_opened", {
        source,
        has_existing_plan: Boolean(spanishJourneyPlan),
      });
    }
    setSpanishJourneyPlannerSource(source);
    setIsJourneyMapOpen(false);
    setIsSpanishJourneyPlannerOpen(true);
  };

  const closeSpanishJourneyPlanner = () => {
    setIsSpanishJourneyPlannerOpen(false);
    if (spanishJourneyPlannerSource === "path") setIsJourneyMapOpen(true);
  };

  const openSpanishJourneyPath = () => {
    if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.capture("spanish_journey_path_opened", {
        has_plan: Boolean(spanishJourneyPlan),
        completed_plan_items: spanishJourneyPlanCompletedCount,
      });
    }
    setIsJourneyMapOpen(true);
  };

  const handleSpanishJourneyUpgrade = () => {
    trackEventWithAttribution(posthog, "upgrade_recovery_action_clicked", {
      action: "upgrade",
      recovery_surface: "spanish_journey_plan",
      trigger: "locked_recommended_chapter",
      chapter_id: spanishJourneyRecommendation?.id || null,
      source: "spanish_journey_plan",
    });
    window.location.assign(
      buildTrackedPaywallUrl({
        extraParams: { chapter_id: spanishJourneyRecommendation?.id },
        source: "spanish_journey_plan",
      })
    );
  };

  const podcastDiscussionTitle =
    podcastDiscussionProject?.title?.trim() ||
    selectedJourneyChapter?.title ||
    "Selected podcast script";
  const podcastDiscussionIndicator = podcastDiscussionId ? (
    <div
      className={`w-full rounded-lg border p-4 text-left shadow-lg ${
        isPodcastDiscussionEnabled
          ? isSchool
            ? "border-school-accent/35 bg-school-accent/10 shadow-school-accent/10"
            : "border-orange-400/35 bg-orange-500/10 shadow-orange-500/10"
          : "border-white/10 bg-white/[0.035] shadow-black/10"
      }`}
      data-learning-mode-selector
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              isPodcastDiscussionEnabled
                ? isSchool
                  ? "bg-school-accent text-school-on-accent"
                  : "bg-[#FD954F] text-[#1C1C1E]"
                : "bg-white/10 text-zinc-400"
            }`}
          >
            <Headphones size={18} />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase text-zinc-500">Podcast context</p>
            <p className="mt-1 line-clamp-1 text-sm font-bold text-white">
              {podcastDiscussionLoadFailed ? "Podcast script selected" : podcastDiscussionTitle}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/20 px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-xs font-bold text-zinc-200">Include in session</p>
          <p className="mt-0.5 text-[11px] leading-snug text-zinc-500">
            {isPodcastDiscussionEnabled
              ? "Chicky will discuss this podcast."
              : "Start normal speaking practice."}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isPodcastDiscussionEnabled}
          aria-label={`Podcast context ${isPodcastDiscussionEnabled ? "on" : "off"}`}
          onClick={(event) => {
            event.stopPropagation();
            setIsPodcastDiscussionEnabled((enabled) => !enabled);
          }}
          className={`relative h-7 w-12 shrink-0 rounded-full border transition-all ${
            isPodcastDiscussionEnabled
              ? isSchool
                ? "border-school-accent bg-school-accent"
                : "border-orange-400 bg-orange-500"
              : "border-white/15 bg-white/10"
          }`}
        >
          <span
            className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              isPodcastDiscussionEnabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  ) : null;
  useEffect(() => {
    if (room) {
      const handleDataReceived = (
        payload: Uint8Array,
        participant?: any,
        kind?: DataPacket_Kind,
        topic?: string
      ) => {
        try {
          const textDecoder = new TextDecoder();
          const messageStr = textDecoder.decode(payload);
          logger.debug("Received data message", { messageStr });

          const data = JSON.parse(messageStr);
          logger.debug("Parsed data", data);

          if (data.type === "canvas_exercise") {
            logger.info("Received canvas_exercise data", data);
            setCanvasExerciseContent(data.content);
            setCanvasExerciseOptions(data.options);
            setShowCanvas(true);
            logger.info("Canvas should now be visible");
          } else if (data.type === "toggle_canvas") {
            logger.info("Received toggle_canvas data", data);
            setShowCanvas(data.show);
            logger.info("Canvas visibility set to", { show: data.show });
          } else if (data.type === "submit_answer") {
            logger.info("Received submit_answer data", data);
            // Store the answer to be submitted to the canvas
            setPendingAnswerSubmission(data.answer);
            logger.info("Answer stored for canvas submission", { answer: data.answer });
          } else {
            logger.info("Received data with unknown type", { type: data.type });
          }
        } catch (e) {
          logger.error("Failed to parse data message", e);
        }
      };

      room.on(RoomEvent.DataReceived, handleDataReceived);
      logger.debug("Data message listener registered");

      return () => {
        room.off(RoomEvent.DataReceived, handleDataReceived);
        logger.debug("Data message listener unregistered");
      };
    }
  }, [room]);

  useEffect(() => {
    if (onCanvasVisibilityChange) {
      onCanvasVisibilityChange(showCanvas);
    }
  }, [showCanvas, onCanvasVisibilityChange]);

  const handleCanvasClose = useCallback(() => {
    setShowCanvas(false);
  }, []);

  const handleAnswerSubmitted = useCallback(() => {
    setPendingAnswerSubmission(null);
    onConversationActivity?.();
  }, [onConversationActivity]);

  const handleTimeLimitPopupClose = useCallback(
    (action?: TimeLimitPopupCloseAction) => {
      dismissTimeLimitPopup();
      if (action === "dismiss" && userEmail && !subscribed) {
        setShowSessionLimitRecovery(true);
        trackEventWithAttribution(posthog, "upgrade_recovery_viewed", {
          recovery_surface: "session_limit_wrap_up",
          trigger: "hard_limit_decline",
          limit_type: timeLimitPopupContext?.limitType,
          freemium_cohort: timeLimitPopupContext?.freemiumCohort,
          streak_bucket:
            typeof currentStreak !== "number"
              ? "unknown"
              : currentStreak >= 7
                ? "7_plus"
                : currentStreak >= 2
                  ? "2_to_6"
                  : String(currentStreak),
          objection_eligible: false,
          source: "time_limit_popup",
        });
      }
    },
    [currentStreak, dismissTimeLimitPopup, posthog, subscribed, timeLimitPopupContext, userEmail]
  );

  const handleSessionLimitRecoveryClose = useCallback(
    (action: "continue_free" | "dismiss") => {
      trackEventWithAttribution(posthog, "upgrade_recovery_action_clicked", {
        action: action === "continue_free" ? "done" : action,
        recovery_surface: "session_limit_wrap_up",
        trigger: "hard_limit_decline",
        limit_type: timeLimitPopupContext?.limitType,
        source: "time_limit_popup",
      });
      setShowSessionLimitRecovery(false);
    },
    [posthog, timeLimitPopupContext]
  );

  const handleSessionLimitRecoveryUpgrade = useCallback(() => {
    trackEventWithAttribution(posthog, "upgrade_recovery_action_clicked", {
      action: "upgrade",
      recovery_surface: "session_limit_wrap_up",
      trigger: "hard_limit_decline",
      limit_type: timeLimitPopupContext?.limitType,
      source: "time_limit_popup",
    });
    setShowSessionLimitRecovery(false);
    window.location.assign(
      buildTrackedPaywallUrl({
        coupon: getFreemiumDiscountCoupon(timeLimitPopupContext?.freemiumCohort),
        extraParams: { limit_type: timeLimitPopupContext?.limitType },
        source: "session_limit_wrap_up",
      })
    );
  }, [posthog, timeLimitPopupContext]);

  if (isImpromptuOpen && selectedImpromptuLanguageCode) {
    return (
      <ImpromptuExperience
        embedded
        initialLanguageCode={selectedImpromptuLanguageCode}
        initialLearnerProfile={selectedLanguageLevel}
        onBack={() => setIsImpromptuOpen(false)}
      />
    );
  }

  return (
    <>
      {showTimeLimitPopup && (
        <TimeLimitPopup
          onClose={handleTimeLimitPopupClose}
          userEmail={userEmail}
          limitType={timeLimitPopupContext?.limitType}
          freemiumCohort={timeLimitPopupContext?.freemiumCohort}
          qualifiedConversationCount={timeLimitPopupContext?.qualifiedConversationCount}
          qualifiedConversationsThisWeek={timeLimitPopupContext?.qualifiedConversationsThisWeek}
        />
      )}
      {showSessionLimitRecovery ? (
        <UpgradeRecoveryCard
          currentStreak={currentStreak}
          limitType={timeLimitPopupContext?.limitType}
          onClose={handleSessionLimitRecoveryClose}
          onUpgrade={handleSessionLimitRecoveryUpgrade}
          surface="session_limit_wrap_up"
        />
      ) : null}
      <HowItWorksModal isOpen={isHowItWorksOpen} onClose={() => setIsHowItWorksOpen(false)} />
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        selectedVoice={selectedVoice}
        setSelectedVoice={setSelectedVoice}
        selectedTeachingStyle={selectedTeachingStyle}
        setSelectedTeachingStyle={setSelectedTeachingStyle}
        nickname={nickname}
        setNickname={setNickname}
        customInstructions={customInstructions}
        setCustomInstructions={setCustomInstructions}
        selectedNoAvatar={selectedNoAvatar}
        selectedLanguageCode={selectedLanguageCode}
        selectedLanguageName={selectedLanguageName}
        isAvatarForcedOff={isAvatarForcedOff}
        backgroundVideoDefaultId={brandVariant === "ecole" ? "ecole-default" : undefined}
        backgroundVideoFallbackUrl={brandVariant === "ecole" ? ECOLE_DEFAULT_VIDEO : undefined}
        allowBackgroundSelection={brandVariant !== "liceo"}
        setSelectedNoAvatar={setSelectedNoAvatar}
        subscribed={subscribed}
        conversationStartChoice={conversationStartChoice}
        setConversationStartChoice={setConversationStartChoice}
        tutorWhiteboardEnabled={tutorWhiteboardEnabled}
        onTutorWhiteboardEnabledChange={onTutorWhiteboardEnabledChange}
        examplesBeforePractice={examplesBeforePractice}
        onExamplesBeforePracticeChange={onExamplesBeforePracticeChange}
      />
      <ProFeatureUpgradeModal
        isOpen={isResumeUpgradeOpen}
        feature="resume_session"
        onClose={() => setIsResumeUpgradeOpen(false)}
        source="listenview_resume_session"
        userId={userId || undefined}
        userEmail={userEmail || undefined}
      />
      {selectedJourney && isSpanishJourney && (
        <SpanishJourneyPathModal
          isOpen={isEffectiveJourneyLearningMode && isJourneyMapOpen}
          onClose={() => setIsJourneyMapOpen(false)}
          journey={selectedJourney}
          progress={journeyProgress}
          selectedChapterId={journeyChapterId}
          subscribed={subscribed}
          plan={spanishJourneyPlan}
          audioReadyPodcastChapterIds={audioReadyJourneyPodcastChapterIds}
          onSelectChapter={handleJourneyChapterSelect}
          onToggleChapterComplete={handleToggleJourneyChapterComplete}
          onUseChapter={() => setIsJourneyMapOpen(false)}
          onOpenPlanner={() => openSpanishJourneyPlanner("path")}
        />
      )}
      <SpanishJourneyPlannerModal
        isOpen={isSpanishJourney && isSpanishJourneyPlannerOpen}
        onClose={closeSpanishJourneyPlanner}
        onBack={closeSpanishJourneyPlanner}
        existingPlan={spanishJourneyPlan}
        generating={isSpanishJourneyPlanGenerating}
        saving={isSpanishJourneyPlanSaving}
        error={spanishJourneyPlanError}
        onGenerate={handleGenerateSpanishJourneyPlan}
        onSave={handleSaveSpanishJourneyPlan}
        onOpenPath={() => {
          setIsSpanishJourneyPlannerOpen(false);
          openSpanishJourneyPath();
        }}
      />
      {canUseDocumentPortal
        ? createPortal(
            <AnimatePresence>
              {isEffectiveJourneyLearningMode &&
                selectedJourney &&
                !isSpanishJourney &&
                isJourneyMapOpen && (
                  <motion.div
                    className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/75 p-2 sm:p-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setIsJourneyMapOpen(false)}
                    role="presentation"
                  >
                    <motion.div
                      role="dialog"
                      aria-modal="true"
                      aria-label="Choose a Language Journey chapter"
                      className="relative flex max-h-[96dvh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-white/10 bg-[#121318] text-left shadow-[0_28px_90px_rgba(0,0,0,0.55)] sm:max-h-[92vh]"
                      initial={{ opacity: 0, scale: 0.97, y: 16 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.97, y: 16 }}
                      transition={{ duration: 0.18 }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="flex flex-col gap-3 border-b border-white/10 bg-white/[0.03] p-3 sm:gap-4 sm:p-6">
                        <div className="flex items-start justify-between gap-3 sm:gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-[10px] font-black uppercase text-zinc-500">
                              <MapIcon size={14} />
                              Language Journey
                              {selectedJourney.releaseStage === "pilot" && (
                                <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] font-bold normal-case text-zinc-400">
                                  Pilot
                                </span>
                              )}
                            </div>
                            <h3 className="mt-1 text-xl font-bold text-white sm:mt-2 sm:text-3xl">
                              {selectedJourney.languageName} chapter path
                            </h3>
                            {selectedJourney.sourceLabel && (
                              <div className="mt-2 flex items-center gap-2">
                                {selectedJourney.sourceLogoSrc && (
                                  <img
                                    src={selectedJourney.sourceLogoSrc}
                                    alt={
                                      selectedJourney.sourceLogoAlt || selectedJourney.sourceLabel
                                    }
                                    className="h-12 w-auto max-w-[13rem] shrink-0 object-contain"
                                  />
                                )}
                                <p className="text-xs font-bold text-zinc-300">
                                  {selectedJourney.sourceLabel}
                                </p>
                              </div>
                            )}
                            <p className="mt-1 text-xs font-semibold text-zinc-500 sm:hidden">
                              {selectedJourneyLevelGroup?.label || selectedJourneyLevel} -{" "}
                              {journeyProgress.completedChapterIds.length}/
                              {selectedJourney.chapters.length} complete -{" "}
                              {journeyCompletionPercent}%
                            </p>
                            <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-zinc-400 sm:block">
                              Pick the next structured chapter. Each step builds grammar and word
                              order through a focused private-tutor style speaking session.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsJourneyMapOpen(false)}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-zinc-400 transition-all hover:bg-white/[0.08] hover:text-white sm:h-11 sm:w-11"
                            aria-label="Close chapter map"
                          >
                            <X size={18} />
                          </button>
                        </div>

                        <div className="hidden gap-3 sm:grid sm:grid-cols-[1fr_auto] sm:items-end">
                          <div>
                            <div className="flex items-center justify-between gap-3 text-xs font-bold text-zinc-400">
                              <span>Overall progress</span>
                              <span>{journeyCompletionPercent}% complete</span>
                            </div>
                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
                              <div
                                className={`h-full rounded-full ${
                                  isSchool ? "bg-school-accent" : "bg-orange-400"
                                }`}
                                style={{
                                  width: `${journeyCompletionPercent}%`,
                                }}
                              />
                            </div>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm font-bold text-zinc-300">
                            {journeyProgress.completedChapterIds.length}/
                            {selectedJourney.chapters.length} chapters
                          </div>
                        </div>
                      </div>

                      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[14rem_1fr]">
                        <div className="min-h-0 overflow-y-auto border-b border-white/10 p-2 sm:p-3 lg:border-b-0 lg:border-r lg:p-4">
                          <div className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
                            {journeyChapterGroups.map((group) => {
                              const completedInLevel = group.chapters.filter((chapter) =>
                                completedJourneyChapterIds.has(chapter.id)
                              ).length;
                              const isActive = group.id === selectedJourneyLevel;

                              return (
                                <button
                                  key={group.id}
                                  type="button"
                                  onClick={() => setSelectedJourneyLevel(group.id)}
                                  className={`min-w-[3.5rem] rounded-full border px-3 py-2 text-center transition-all sm:min-w-[8rem] sm:rounded-xl sm:px-4 sm:py-3 sm:text-left lg:min-w-0 ${
                                    isActive
                                      ? "border-white/25 bg-white text-black"
                                      : "border-white/10 bg-white/[0.035] text-zinc-300 hover:border-white/20 hover:bg-white/[0.07]"
                                  }`}
                                >
                                  <span className="block text-sm font-black">{group.label}</span>
                                  <span className="mt-1 hidden text-[11px] font-semibold opacity-70 sm:block">
                                    {completedInLevel}/{group.chapters.length} complete
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          <p className="mt-4 hidden text-xs leading-relaxed text-zinc-500 lg:block">
                            Free users can try the first{" "}
                            {selectedJourneyLevelGroup?.freePreviewChapters ||
                              selectedJourney.cefrLevels[0]?.freePreviewChapters ||
                              2}{" "}
                            chapters in each{" "}
                            {selectedJourney.groupingMode === "course_units"
                              ? "course unit"
                              : "CEFR level"}
                            .
                          </p>
                        </div>

                        <div className="min-h-0 overflow-y-auto p-3 pb-24 sm:p-6 sm:pb-28">
                          {selectedJourneyLevelGroup && (
                            <>
                              <div className="mb-3 max-w-3xl sm:mb-6">
                                <p className="text-sm font-bold text-white">
                                  {selectedJourneyLevelGroup.title}
                                </p>
                                <p className="mt-1 hidden text-sm leading-relaxed text-zinc-400 sm:block">
                                  {selectedJourneyLevelGroup.canDo}
                                </p>
                              </div>

                              <div className="relative pb-4">
                                <div className="absolute bottom-0 top-0 hidden w-px bg-gradient-to-b from-white/10 via-white/20 to-white/5 md:left-1/2 md:block" />
                                <div className="space-y-3 sm:space-y-5">
                                  {selectedJourneyLevelGroup.chapters.map((chapter, index) => {
                                    const isCompleted = completedJourneyChapterIds.has(chapter.id);
                                    const isSelected = selectedJourneyChapter?.id === chapter.id;
                                    const isUnlocked = isLanguageJourneyChapterUnlocked(
                                      chapter,
                                      subscribed
                                    );
                                    const isLeft = index % 2 === 0;
                                    const hasAudioTrack = audioReadyJourneyPodcastChapterIds.has(
                                      chapter.id
                                    );

                                    return (
                                      <div
                                        key={chapter.id}
                                        className={`relative flex md:pl-0 ${
                                          isLeft
                                            ? "md:justify-start md:pr-[53%]"
                                            : "md:justify-end md:pl-[53%]"
                                        }`}
                                      >
                                        <span
                                          className={`absolute left-5 top-5 z-10 hidden h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border text-sm font-black shadow-lg md:left-1/2 md:flex ${
                                            isCompleted
                                              ? "border-emerald-300/50 bg-emerald-400 text-black"
                                              : isSelected
                                                ? isSchool
                                                  ? "border-school-accent/70 bg-school-accent text-school-on-accent"
                                                  : "border-orange-300/70 bg-orange-400 text-black"
                                                : isUnlocked
                                                  ? "border-white/20 bg-zinc-900 text-zinc-200"
                                                  : "border-white/10 bg-zinc-950 text-zinc-500"
                                          }`}
                                        >
                                          {isCompleted ? (
                                            <CheckCircle2 size={18} />
                                          ) : isUnlocked ? (
                                            chapter.cefrLevelChapterNumber
                                          ) : (
                                            <Lock size={15} />
                                          )}
                                        </span>

                                        <div
                                          className={`w-full rounded-xl border p-3 text-left transition-all sm:p-4 ${
                                            isSelected
                                              ? isSchool
                                                ? "border-school-accent/80 bg-school-accent/10 shadow-[0_0_0_3px_rgba(255,182,6,0.14)]"
                                                : "border-orange-300/80 bg-orange-500/10 shadow-[0_0_0_3px_rgba(251,146,60,0.16)]"
                                              : "border-white/10 bg-white/[0.035] hover:border-white/20 hover:bg-white/[0.07]"
                                          } ${!isUnlocked ? "cursor-not-allowed opacity-55" : ""}`}
                                        >
                                          <div className="flex flex-wrap items-center gap-2">
                                            <button
                                              type="button"
                                              disabled={!isUnlocked}
                                              onClick={() => handleJourneyChapterSelect(chapter)}
                                              aria-current={isSelected ? "step" : undefined}
                                              aria-pressed={isSelected}
                                              aria-label={`Select Chapter ${chapter.chapterNumber}: ${chapter.title}`}
                                              className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left focus:outline-none"
                                            >
                                              <span
                                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-black md:hidden ${
                                                  isCompleted
                                                    ? "border-emerald-300/50 bg-emerald-400 text-black"
                                                    : isSelected
                                                      ? isSchool
                                                        ? "border-school-accent/70 bg-school-accent text-school-on-accent"
                                                        : "border-orange-300/70 bg-orange-400 text-black"
                                                      : isUnlocked
                                                        ? "border-white/20 bg-zinc-900 text-zinc-200"
                                                        : "border-white/10 bg-zinc-950 text-zinc-500"
                                                }`}
                                              >
                                                {isCompleted ? (
                                                  <CheckCircle2 size={14} />
                                                ) : isUnlocked ? (
                                                  chapter.cefrLevelChapterNumber
                                                ) : (
                                                  <Lock size={13} />
                                                )}
                                              </span>
                                              <span className="text-sm font-bold text-white sm:text-base">
                                                {chapter.title}
                                              </span>
                                              {isSelected && (
                                                <span
                                                  className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
                                                    isSchool
                                                      ? "bg-school-accent text-school-on-accent"
                                                      : "bg-orange-400 text-black"
                                                  }`}
                                                >
                                                  Selected
                                                </span>
                                              )}
                                              <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-black uppercase text-zinc-400">
                                                {chapter.cefrLevel}.{chapter.cefrLevelChapterNumber}
                                              </span>
                                            </button>
                                            {hasAudioTrack && (
                                              <a
                                                href={`/podcasts?languageCode=${encodeURIComponent(
                                                  selectedLanguageCode
                                                )}&chapterId=${encodeURIComponent(chapter.id)}`}
                                                aria-label={`Open ${chapter.title} podcast`}
                                                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#FD954F] px-3 text-black shadow-lg shadow-orange-950/20 transition hover:bg-[#F28747] focus:outline-none focus:ring-2 focus:ring-orange-300/35"
                                              >
                                                <Headphones size={16} />
                                                <span className="text-[10px] font-black uppercase tracking-normal">
                                                  Beta
                                                </span>
                                              </a>
                                            )}
                                            {chapter.isFreePreview && (
                                              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-black uppercase text-emerald-300">
                                                Free
                                              </span>
                                            )}
                                            {!isUnlocked && (
                                              <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-black uppercase text-zinc-500">
                                                Pro
                                              </span>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            disabled={!isUnlocked}
                                            onClick={() => handleJourneyChapterSelect(chapter)}
                                            className="mt-2 block w-full text-left focus:outline-none"
                                          >
                                            <span className="block line-clamp-2 text-xs leading-relaxed text-zinc-400 sm:text-sm">
                                              {getLanguageJourneyChapterDisplayDescription(
                                                selectedJourney,
                                                chapter
                                              )}
                                            </span>
                                            <span className="mt-3 hidden text-xs font-semibold text-zinc-500 sm:block">
                                              {getLanguageJourneyChapterDisplayFocus(
                                                selectedJourney,
                                                chapter
                                              )}
                                            </span>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 border-t border-white/10 bg-black/35 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                        <div className="min-w-0">
                          <p
                            className={`text-xs font-black uppercase ${
                              isSchool ? "text-school-accent" : "text-orange-300"
                            }`}
                          >
                            Selected chapter
                          </p>
                          <p className="line-clamp-1 text-sm font-bold text-white sm:mt-1">
                            {`Chapter ${selectedJourneyChapter?.chapterNumber || 1}: ${
                              selectedJourneyChapter?.title || "First chapter"
                            }`}
                          </p>
                          {selectedJourney.sourceLabel && (
                            <div className="mt-1 flex min-w-0 items-center gap-2">
                              {selectedJourney.sourceLogoSrc && (
                                <img
                                  src={selectedJourney.sourceLogoSrc}
                                  alt={selectedJourney.sourceLogoAlt || selectedJourney.sourceLabel}
                                  className="h-8 w-auto max-w-[10rem] shrink-0 object-contain opacity-95"
                                />
                              )}
                              <p className="line-clamp-1 text-xs font-semibold text-zinc-500">
                                {selectedJourney.sourceLabel}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <JourneyChapterCompletionButton
                            completed={selectedJourneyChapterCompleted}
                            disabled={!selectedJourneyChapter}
                            onToggle={handleToggleJourneyChapterComplete}
                          />
                          {selectedJourneyChapter && (
                            <a
                              href={`/podcasts?languageCode=${encodeURIComponent(
                                selectedLanguageCode
                              )}&chapterId=${encodeURIComponent(selectedJourneyChapter.id)}`}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-bold text-zinc-200 transition-all hover:border-white/20 hover:bg-white/[0.08] sm:w-auto"
                            >
                              <Headphones size={15} />
                              Podcast beta
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsJourneyMapOpen(false)}
                            className={`inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-bold text-white transition-all sm:w-auto ${accentPrimaryButtonClass} ${accentPrimaryButtonHoverClass}`}
                          >
                            Use chapter
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
            </AnimatePresence>,
            document.body
          )
        : null}

      <div
        className={`${showCanvas ? "min-h-screen" : "flex-1"} flex flex-col overflow-visible min-h-0`}
      >
        {showCanvas && isRecording ? (
          <div className="h-full flex flex-col lg:flex-row overflow-hidden">
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 350, damping: 30 }}
              className={`
                ${isTranscriptCollapsed ? "h-auto flex-shrink-0" : "h-2/5"}
                lg:h-full lg:w-1/3 bg-[#1c202e] border-b lg:border-b-0 lg:border-r border-gray-800 flex flex-col z-10`}
            >
              <RoomContext.Provider value={room}>
                {isRecording && isConnected && (
                  <TrackedRoomAudioRenderer
                    activeSession={isRecording && isConnected}
                    roomName={room.name || null}
                    surface="listen-view-canvas"
                  />
                )}
                <div className="relative flex flex-col h-full overflow-hidden">
                  {shouldBootstrapAvatar && (
                    <div className="pointer-events-none absolute left-1/2 top-4 z-0 -translate-x-1/2 opacity-0">
                      <VoiceAgent
                        room={room}
                        isRecording={isRecording}
                        renderAudioRenderer={false}
                      />
                    </div>
                  )}
                  {isConnected && (
                    <>
                      <div
                        className={`flex-1 overflow-hidden lg:flex ${isTranscriptCollapsed ? "hidden" : "flex"}`}
                      >
                        <TranscriptionView
                          onClose={disconnectFromLiveKit}
                          connectionState={connectionState}
                          connectionError={connectionError}
                          retryConnection={retryConnection}
                          audioTrackError={audioTrackError}
                          audioRetryCount={audioRetryCount}
                          retryAudioTrack={retryAudioTrack}
                          clearAudioErrors={clearAudioErrors}
                          sessionDuration={recordingTime}
                          selectedLanguageCode={selectedLanguageCode}
                          selectedLanguageLevel={selectedLanguageLevel}
                          isActiveSession={isRecording && isConnected}
                          // The split layout already renders the avatar above the transcript panel.
                          // Disable the header avatar here to avoid a second Spatius viewer connection.
                          disableAvatar={true}
                          isMuted={isMuted}
                          onToggleMute={toggleMute}
                          setMicrophoneMuted={setMicrophoneMuted}
                          enableVoiceNotes={voiceNoteTurnsEnabled}
                          onConversationActivity={onConversationActivity}
                          renderRoomAudio={false}
                          tutorWhiteboardEnabled={tutorWhiteboardEnabled}
                          sessionMode={sessionMode || undefined}
                          learningMode={effectiveActiveSessionLearningMode}
                          onLearningModeChange={handleActiveLearningModeChange}
                          defaultTranslationLanguageCode={guidedInstructionLanguageCode}
                          instructionLanguageCode={nativeLanguageCode}
                          translationLanguageCode={translationLanguageCode}
                          onTranslationLanguageChange={onTranslationLanguageChange}
                          translationsEnabled={transcriptTranslationsEnabled}
                          onTranslationsEnabledChange={setTranscriptTranslationsEnabled}
                          continuationConversation={continuationConversation}
                          previousTutorMessages={previousTutorMessages}
                        />
                      </div>
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        onClick={() => setIsTranscriptCollapsed((prev) => !prev)}
                        className="lg:hidden w-full text-center p-2 border-t border-gray-800 flex items-center justify-center text-gray-400 hover:text-white text-sm"
                      >
                        <motion.span layout="position">
                          {isTranscriptCollapsed ? "Show" : "Hide"} Transcript
                        </motion.span>
                        {isTranscriptCollapsed ? (
                          <ChevronUp className="ml-2" size={16} />
                        ) : (
                          <ChevronDown className="ml-2" size={16} />
                        )}
                      </motion.button>
                    </>
                  )}
                </div>
              </RoomContext.Provider>
            </motion.div>

            <div className="flex-1 lg:w-2/3 overflow-auto bg-[#0F0F14]">
              <FillInTheGapCanvas
                exerciseContent={canvasExerciseContent}
                onClose={handleCanvasClose}
                exerciseOptions={canvasExerciseOptions}
                selectedLanguage={selectedLanguageName || "English"}
                fromLanguageName={fromLanguageName}
                translationLanguageCode={visualTranslationLanguageCode}
                selectedLanguageCode={selectedLanguageCode}
                onTranslationLanguageChange={onTranslationLanguageChange}
                selectedLanguageLevel={selectedLanguageLevel}
                pendingAnswerSubmission={pendingAnswerSubmission}
                onAnswerSubmitted={handleAnswerSubmitted}
                room={room}
              />
            </div>
          </div>
        ) : showCanvas && !isRecording ? (
          <div className="flex-1 flex flex-col">
            {/* Canvas without voice agent - standalone mode */}
            <FillInTheGapCanvas
              exerciseContent={canvasExerciseContent}
              onClose={handleCanvasClose}
              exerciseOptions={canvasExerciseOptions}
              selectedLanguage={selectedLanguageName || "English"}
              fromLanguageName={fromLanguageName}
              translationLanguageCode={visualTranslationLanguageCode}
              selectedLanguageCode={selectedLanguageCode}
              onTranslationLanguageChange={onTranslationLanguageChange}
              selectedLanguageLevel={selectedLanguageLevel}
              pendingAnswerSubmission={pendingAnswerSubmission}
              onAnswerSubmitted={handleAnswerSubmitted}
              room={room}
            />
          </div>
        ) : (
          <>
            <div className="flex-1 flex flex-col md:flex-row overflow-visible min-h-0">
              {showTranscript && (
                <div className="w-full md:w-1/2 bg-[#1c202e] border-b md:border-b-0 md:border-r border-gray-800 overflow-y-auto p-3 md:p-4 z-50">
                  <TranscriptText
                    segments={segments}
                    currentTime={currentTime}
                    showTranslation={showTranslation}
                    onSegmentClick={onSegmentClick}
                    onExplainSegment={onExplainSegment}
                    getSegmentClasses={getSegmentClasses}
                    showTimestamps={false}
                  />
                </div>
              )}

              <div
                className={`${showTranscript ? "w-full md:w-1/2" : "w-full flex-1"} flex flex-col relative min-h-0`}
              >
                <div
                  className={`relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-visible px-4 pb-4 ${
                    preSessionVariant !== "default"
                      ? "pt-8 md:pt-12"
                      : shouldShowManualStartFallback
                        ? shellLayout === "inline"
                          ? "pt-24 md:pt-28"
                          : "pt-20 md:pt-24"
                        : "pt-20 md:pt-24"
                  }`}
                >
                  {audioTrackError && (
                    <div className="relative z-40 w-full max-w-md shrink-0" role="status">
                      <AudioTrackError
                        audioTrackError={audioTrackError}
                        audioRetryCount={audioRetryCount}
                        retryAudioTrack={retryAudioTrack}
                        clearAudioErrors={clearAudioErrors}
                      />
                    </div>
                  )}
                  {shouldShowReadyScreen ? (
                    <>
                      {preSessionVariant === "start-only" && (
                        <div className="relative z-30 flex flex-col items-center justify-center text-center">
                          <StartOnlyPracticeEntry
                            pending={isStartSpeakingPending}
                            primaryButtonClassName={accentPrimaryButtonClass}
                            primaryButtonHoverClassName={accentPrimaryButtonHoverClass}
                            targetLanguageName={selectedLanguageName || "your language"}
                            instructionLanguageName={fromLanguageName || "English"}
                            onStart={() => {
                              posthog?.capture(
                                embedAnalyticsEventPrefix
                                  ? `${embedAnalyticsEventPrefix}_start_clicked`
                                  : "embed_demo_start_clicked",
                                {
                                  analytics_surface: analyticsSurface,
                                  target_language_code: selectedLanguageCode,
                                  instruction_language_code: nativeLanguageCode,
                                  selected_language_level: selectedLanguageLevel,
                                }
                              );
                              void handleStartConversation();
                            }}
                          />
                        </div>
                      )}
                      {preSessionVariant === "configurable-start" && (
                        <div className="relative z-30 flex w-full flex-col items-center justify-center text-center">
                          <ConfigurableDemoEntry
                            pending={isStartSpeakingPending}
                            primaryButtonClassName={accentPrimaryButtonClass}
                            primaryButtonHoverClassName={accentPrimaryButtonHoverClass}
                            targetLanguageCode={selectedLanguageCode}
                            instructionLanguageCode={nativeLanguageCode}
                            onTargetLanguageChange={setSelectedLanguageCode}
                            onInstructionLanguageChange={setNativeLanguageCode}
                            onStart={() => {
                              const sourceParams = new URLSearchParams(window.location.search);
                              trackAcquisitionDemoEvent(
                                posthog,
                                ACQUISITION_DEMO_EVENTS.speakingStarted,
                                {
                                  demoId: "acquisition-speaking-demo",
                                  demoKind: "live_speaking",
                                  pagePath: sourceParams.get("sourcePath") || "unknown",
                                  pageFamily: sourceParams.get("sourceFamily") || "unknown",
                                  placement: sourceParams.get("placement") || "embedded_demo",
                                  experimentKey: "acquisition_live_demo_v1",
                                  experimentVariant:
                                    sourceParams.get("experimentVariant") || undefined,
                                  targetLanguageCode: selectedLanguageCode,
                                  instructionLanguageCode: nativeLanguageCode,
                                  languageLevel: selectedLanguageLevel,
                                }
                              );
                              onCommitLanguagePreferences?.();
                              void handleStartConversation();
                            }}
                          />
                        </div>
                      )}
                      <div
                        className={`relative z-30 flex w-full min-w-0 flex-col items-center justify-center text-center ${
                          preSessionVariant !== "default" ? "hidden" : ""
                        }`}
                      >
                        {/* Mobile class badge */}
                        {activeClass && (
                          <div className="md:hidden mb-3">
                            <span
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/70 border-2 text-xs font-semibold ${
                                accentBorderClass
                              } ${accentTextSoftClass}`}
                            >
                              Using class: {activeClass.class_name}
                            </span>
                          </div>
                        )}
                        {effectivePracticeSetupStep === "language" ? (
                          <>
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.05 }}
                              data-chicky-obstacle
                              className={`relative mb-6 w-full items-center gap-8 text-left ${
                                isUnauthenticatedFirstUse
                                  ? "max-w-3xl"
                                  : "grid max-w-5xl md:grid-cols-[0.8fr_1.2fr]"
                              }`}
                            >
                              {!isUnauthenticatedFirstUse && (
                                <div className="relative hidden md:block">
                                  <div className="relative max-w-2xl">
                                    <h1 className="text-4xl font-black leading-tight tracking-normal text-white lg:text-5xl">
                                      Which language do you want to speak?
                                    </h1>
                                    <p className="mt-5 max-w-md text-base font-medium leading-7 text-zinc-300">
                                      Start a private conversation matched to your level. Speak, get
                                      one useful correction, and try again.
                                    </p>
                                    <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-zinc-500">
                                      <span>Voice first</span>
                                      <span>Live corrections</span>
                                      <span>70+ languages</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="flex w-full flex-col items-center md:items-stretch">
                                <div
                                  className={`mb-5 text-center md:hidden ${
                                    isUnauthenticatedFirstUse ? "hidden" : ""
                                  }`}
                                >
                                  <div
                                    className={`mx-auto mb-4 h-px w-24 ${
                                      isSchool ? "bg-school-accent/50" : "bg-orange-300/50"
                                    }`}
                                  />
                                  <h1 className="text-3xl font-black leading-tight tracking-normal text-white">
                                    Which language do you want to speak?
                                  </h1>
                                </div>

                                <motion.div
                                  animate={
                                    animateLanguageShake
                                      ? {
                                          x: [0, -6, 6, -6, 6, -3, 3, 0],
                                          transition: { duration: 0.5 },
                                        }
                                      : {}
                                  }
                                  className={`relative w-full overflow-hidden p-3 md:p-5 ${
                                    isUnauthenticatedFirstUse
                                      ? "bg-transparent"
                                      : `rounded-lg border bg-[#0d0d10]/90 shadow-xl ${
                                          isSchool
                                            ? "border-school-blue/30 shadow-school-blue/10"
                                            : "border-orange-500/25 shadow-orange-500/10"
                                        }`
                                  }`}
                                >
                                  <div className="relative mb-5 flex items-end gap-4">
                                    <div>
                                      <p
                                        className={
                                          isUnauthenticatedFirstUse
                                            ? "text-[10px] font-bold uppercase tracking-[0.18em] text-orange-200/75"
                                            : "text-xs font-semibold text-zinc-500"
                                        }
                                      >
                                        {isUnauthenticatedFirstUse
                                          ? "A private tutor for everyone"
                                          : "Popular choices"}
                                      </p>
                                      {isUnauthenticatedFirstUse ? (
                                        <h1 className="mt-1 text-xl font-bold tracking-normal text-white md:text-2xl">
                                          Choose a language
                                        </h1>
                                      ) : (
                                        <h2 className="mt-1 text-xl font-bold tracking-normal text-white md:text-2xl">
                                          Choose a language
                                        </h2>
                                      )}
                                    </div>
                                  </div>

                                  <div className="relative grid grid-cols-3 gap-3">
                                    {FLAG_LANGUAGE_OPTIONS.map((language) => (
                                      <motion.button
                                        key={language.code}
                                        type="button"
                                        onClick={() => {
                                          setAnimateLanguageShake(false);
                                          handleSetupLanguageSelection(language.code);
                                        }}
                                        whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                                        whileTap={shouldReduceMotion ? undefined : { scale: 0.96 }}
                                        className={`group flex min-h-[98px] flex-col items-center justify-center gap-2.5 rounded-lg border p-3 text-center transition-all ${
                                          isUnauthenticatedFirstUse
                                            ? "border-white/10 bg-black/15 hover:border-orange-300/40 hover:bg-white/[0.06]"
                                            : isSchool
                                              ? "border-school-blue/25 bg-white/[0.04] hover:border-school-accent/50 hover:bg-white/[0.09]"
                                              : "border-white/10 bg-white/[0.04] hover:border-orange-400/50 hover:bg-white/[0.09]"
                                        }`}
                                        aria-label={`Choose ${language.name}`}
                                      >
                                        <span className="flex h-10 w-14 items-center justify-center overflow-hidden rounded-[3px] shadow-md transition-transform group-hover:scale-110">
                                          <img
                                            src={language.flagSrc}
                                            alt=""
                                            className="h-full w-full object-cover"
                                            loading="eager"
                                          />
                                        </span>
                                        <span className="text-sm font-bold leading-tight text-white md:text-sm">
                                          {language.name}
                                        </span>
                                      </motion.button>
                                    ))}
                                  </div>

                                  <ResponsiveLanguagePicker
                                    open={isLanguageDropdownOpen}
                                    onOpenChange={(open) => {
                                      if (open) setAnimateLanguageShake(false);
                                      setIsLanguageDropdownOpen(open);
                                    }}
                                    trigger={
                                      <button
                                        type="button"
                                        className={`group relative mt-3 inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-lg border px-5 py-3 text-sm font-bold text-white transition-colors md:text-base ${
                                          isSchool
                                            ? "border-school-accent/45 bg-school-blue/35 shadow-school-blue/25 hover:border-school-accent/70 hover:bg-school-blue/50 hover:shadow-school-accent/20"
                                            : "border-orange-400/45 bg-orange-500/20 shadow-orange-500/20 hover:border-orange-300/70 hover:bg-orange-500/30 hover:shadow-orange-400/25"
                                        }`}
                                      >
                                        <span className="relative">All languages</span>
                                        <span className="relative rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-black text-black shadow-sm ring-1 ring-black/5">
                                          70+
                                        </span>
                                        <ChevronDown
                                          size={17}
                                          className="relative transition-transform group-hover:translate-y-0.5"
                                        />
                                      </button>
                                    }
                                    languages={languages}
                                    selectedLanguageCode={selectedLanguageCode}
                                    onSelectLanguage={handleSetupLanguageSelection}
                                    searchTerm={languageSearchTerm}
                                    onSearchTermChange={setLanguageSearchTerm}
                                    title="Choose a practice language"
                                    brandVariant={brandVariant}
                                  />
                                </motion.div>
                                <button
                                  type="button"
                                  onClick={() => setIsHowItWorksOpen(true)}
                                  className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 self-center px-2 text-sm font-semibold text-white/60 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60"
                                >
                                  <Play size={14} aria-hidden="true" />
                                  See how ChickyTutor works
                                </button>
                              </div>
                            </motion.div>
                          </>
                        ) : null}

                        {shouldShowManualStartFallback && selectedLanguageCode && (
                          <div className="mb-6 flex w-full min-w-0 flex-col items-center gap-4 animate-fade-in">
                            <header
                              data-chicky-obstacle
                              className="flex w-full max-w-2xl flex-col items-center px-2 text-center"
                            >
                              <h1 className="flex flex-wrap items-baseline justify-center gap-x-2.5 gap-y-1">
                                <span className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl md:text-4xl">
                                  Let&apos;s learn
                                </span>
                                {targetLanguageHeaderControl}
                              </h1>
                            </header>
                            <div
                              data-chicky-obstacle
                              className="flex w-full max-w-md flex-col items-center gap-3 text-center text-[13px] font-medium text-white/40"
                            >
                              <div className="flex items-end justify-center gap-5">
                                <span className="flex min-w-0 flex-col items-center gap-0.5">
                                  <span
                                    className="flex h-6 items-center justify-center px-1.5 text-center text-[9px] font-bold uppercase leading-tight text-white/35"
                                    aria-label="Instruction language"
                                  >
                                    <span aria-hidden="true">
                                      Instruction
                                      <br />
                                      Language
                                    </span>
                                  </span>
                                  {instructionLanguagePicker}
                                </span>
                                <span className="flex min-w-0 flex-col items-center gap-0.5">
                                  <span className="flex h-6 items-center justify-center whitespace-nowrap px-1.5 text-[9px] font-bold uppercase text-white/35">
                                    Settings
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setIsSettingsOpen(true)}
                                    className={`${setupInlineControlBaseClass} w-8 justify-center px-0 text-white/60 hover:text-white`}
                                    aria-label="Open settings"
                                    title="Settings"
                                  >
                                    <Settings className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </span>
                              </div>
                            </div>

                            {!shouldRenderSessionTranscript ? (
                              <div className="animate-fade-in relative mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 md:px-6 md:py-7 lg:px-0">
                                {isEffectiveJourneyLearningMode &&
                                  selectedJourney &&
                                  (isSpanishJourney ? (
                                    <SpanishJourneyOverviewCard
                                      plan={spanishJourneyPlan}
                                      recommendedChapter={
                                        spanishJourneyPlan?.status === "active"
                                          ? spanishJourneyRecommendation
                                          : null
                                      }
                                      selectedChapter={selectedJourneyChapter}
                                      hasExplicitChapterSelection={
                                        explicitlySelectedJourneyChapterRef.current ===
                                        selectedJourneyChapter?.id
                                      }
                                      completedPlanItems={spanishJourneyPlanCompletedCount}
                                      recommendedChapterUnlocked={isLanguageJourneyChapterUnlocked(
                                        spanishJourneyRecommendation,
                                        subscribed
                                      )}
                                      onOpenPlanner={() => openSpanishJourneyPlanner("main_card")}
                                      onOpenPath={openSpanishJourneyPath}
                                      onUpgrade={handleSpanishJourneyUpgrade}
                                    />
                                  ) : (
                                    <div
                                      data-chicky-obstacle
                                      className="mb-5 flex w-full flex-col gap-4 rounded-lg border border-white/10 bg-[#09090b]/85 p-4 text-left shadow-2xl shadow-black/30 ring-1 ring-white/5 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between sm:p-5"
                                      onClick={(event) => event.stopPropagation()}
                                    >
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2 text-[10px] font-black uppercase text-zinc-500">
                                          <BookOpen size={13} />
                                          Language Journey
                                          {selectedJourney.releaseStage === "pilot" && (
                                            <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] font-bold normal-case text-zinc-400">
                                              Pilot
                                            </span>
                                          )}
                                        </div>
                                        <p className="mt-1 truncate text-base font-bold text-white">
                                          {`Chapter ${selectedJourneyChapter?.chapterNumber || 1}: ${
                                            selectedJourneyChapter?.title || "First chapter"
                                          }`}
                                        </p>
                                        {genericJourneyRecommendation?.id ===
                                          selectedJourneyChapter?.id && (
                                          <p className="mt-1 text-xs font-semibold text-orange-200/65">
                                            Chicky suggests this as your next incomplete chapter.
                                          </p>
                                        )}
                                        {selectedJourney.sourceLabel && (
                                          <div className="mt-2 flex items-center gap-2">
                                            {selectedJourney.sourceLogoSrc && (
                                              <img
                                                src={selectedJourney.sourceLogoSrc}
                                                alt={
                                                  selectedJourney.sourceLogoAlt ||
                                                  selectedJourney.sourceLabel
                                                }
                                                className="h-10 w-auto max-w-[12rem] shrink-0 object-contain opacity-95"
                                              />
                                            )}
                                            <p className="text-xs font-semibold text-zinc-500">
                                              {selectedJourney.sourceLabel}
                                            </p>
                                          </div>
                                        )}
                                        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-semibold text-zinc-500">
                                          <span>
                                            {journeyProgress.completedChapterIds.length}/
                                            {selectedJourney.chapters.length} complete
                                          </span>
                                          <span className="h-1 w-1 rounded-full bg-zinc-700" />
                                          <span>{journeyCompletionPercent}% finished</span>
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-2 sm:flex-row">
                                        {selectedJourneyChapter && (
                                          <a
                                            href={`/podcasts?languageCode=${encodeURIComponent(
                                              selectedLanguageCode
                                            )}&chapterId=${encodeURIComponent(
                                              selectedJourneyChapter.id
                                            )}`}
                                            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-bold text-zinc-200 transition-all hover:border-white/20 hover:bg-white/[0.08]"
                                          >
                                            <Headphones size={17} />
                                            Podcast beta
                                          </a>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => setIsJourneyMapOpen(true)}
                                          className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white transition-all ${accentPrimaryButtonClass} ${accentPrimaryButtonHoverClass}`}
                                        >
                                          <MapIcon size={17} />
                                          Choose chapter
                                        </button>
                                      </div>
                                    </div>
                                  ))}

                                {/* HERO BANNER - SPEAKING PRACTICE */}
                                <motion.div
                                  data-chicky-surface
                                  className={`relative mb-6 w-full overflow-hidden rounded-lg border bg-[#0b0b0e]/75 shadow-xl transition-colors ${
                                    isSchool ? "border-school-blue/25" : "border-white/10"
                                  }`}
                                >
                                  <div
                                    className={`relative z-10 flex min-h-[21.5rem] flex-col items-center justify-center gap-7 p-6 sm:min-h-[23rem] sm:p-8 md:min-h-[25rem] md:px-12 md:py-10 lg:px-14 lg:py-11 ${
                                      shellLayout === "inline"
                                        ? "md:gap-10"
                                        : "md:flex-row md:justify-between md:gap-8 lg:gap-10 xl:gap-12 2xl:gap-16"
                                    }`}
                                  >
                                    {/* Left Side: Practice command */}
                                    <div
                                      className={`relative hidden w-full min-w-0 flex-1 text-center md:block ${
                                        shellLayout === "inline" ? "" : "md:text-left"
                                      }`}
                                    >
                                      <div data-chicky-obstacle className="relative z-10">
                                        <div
                                          className={`mb-7 flex flex-col items-center gap-5 lg:gap-6 ${
                                            shellLayout === "inline"
                                              ? "md:flex-row md:justify-center"
                                              : "md:items-start xl:flex-row xl:items-center xl:justify-start"
                                          }`}
                                        >
                                          <VoiceFieldMic
                                            active={isRecording && isConnected}
                                            pending={isStartSpeakingPending}
                                            muted={isMuted}
                                            variant={brandVariant}
                                            hueSeed={selectedLanguageCode}
                                          />
                                          <div className="min-w-0">
                                            {brandVariant === "liceo" ? (
                                              <div className="max-w-sm text-3xl font-black leading-tight text-white lg:text-4xl">
                                                {brandName}
                                              </div>
                                            ) : (
                                              <div className="text-4xl font-black leading-none tracking-normal text-white lg:text-5xl xl:text-6xl">
                                                CHICKY
                                                <span
                                                  className={
                                                    isSchool
                                                      ? "text-school-accent"
                                                      : "text-orange-400"
                                                  }
                                                >
                                                  TUTOR
                                                </span>
                                              </div>
                                            )}
                                            <p className="mt-2 text-sm font-medium text-zinc-400 lg:text-base">
                                              {brandVariant === "liceo"
                                                ? "Tu práctica de inglés entre clases"
                                                : "Your AI language tutor"}
                                            </p>
                                          </div>
                                        </div>

                                        <h2 className="hidden max-w-2xl text-2xl font-normal tracking-normal text-white sm:block lg:text-3xl xl:text-4xl 2xl:text-5xl">
                                          <span className="block whitespace-nowrap text-[1.12em] leading-none">
                                            <span className="font-bold">Speak</span>,
                                          </span>
                                          <span
                                            className={`mt-0.5 block whitespace-nowrap text-[0.92em] leading-none ${
                                              isSchool ? "text-school-soft" : "text-orange-200"
                                            }`}
                                          >
                                            get feedback &amp; improve
                                          </span>
                                        </h2>
                                      </div>
                                    </div>

                                    {/* Right Side: CTA */}
                                    <div
                                      data-chicky-obstacle
                                      className={`flex w-full min-w-0 flex-col items-stretch gap-3.5 ${
                                        shellLayout === "inline"
                                          ? "md:max-w-2xl"
                                          : "mx-auto max-w-2xl md:mx-0 md:w-[20rem] md:max-w-none md:flex-shrink-0 lg:w-[24rem] xl:w-[28rem] 2xl:w-[32rem]"
                                      }`}
                                    >
                                      {podcastDiscussionIndicator}

                                      {/* The pro/stealth model selector is intentionally hidden from production UX.
                                          Keep selectedModel plumbing in Fullscreen/useLiveKitAgent for future A/B tests. */}
                                      <div
                                        className="relative grid grid-cols-3 gap-1 rounded-lg border border-white/10 bg-black/25 p-1"
                                        data-learning-mode-selector
                                      >
                                        <button
                                          type="button"
                                          title={`Guided mode: the tutor explains in ${
                                            compactGuidedInstructionLanguageName
                                          } and asks you to speak ${
                                            selectedLanguageName || "the target language"
                                          }.`}
                                          aria-label={`Guided mode. Instructions in ${
                                            compactGuidedInstructionLanguageName
                                          }.`}
                                          aria-pressed={
                                            !isPodcastDiscussionActive &&
                                            !isConversationLearningMode &&
                                            !isEffectiveJourneyLearningMode
                                          }
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleLearningModeChange("guided");
                                          }}
                                          className={`relative min-h-12 rounded-lg px-2.5 py-1.5 text-center transition-colors ${
                                            !isConversationLearningMode &&
                                            !isEffectiveJourneyLearningMode
                                              ? "bg-white text-black"
                                              : "text-zinc-400 hover:bg-white/10 hover:text-white"
                                          }`}
                                        >
                                          <span className="block text-xs font-bold leading-tight">
                                            Guided
                                          </span>
                                          <span
                                            className={`block text-[10px] font-medium leading-tight mt-0.5 ${
                                              !isConversationLearningMode &&
                                              !isEffectiveJourneyLearningMode
                                                ? "text-zinc-600"
                                                : "text-zinc-400"
                                            }`}
                                          >
                                            {`Tutor helps in ${compactGuidedInstructionLanguageName}`}
                                          </span>
                                        </button>

                                        <button
                                          type="button"
                                          title={`Conversation mode: sets instructions to ${
                                            selectedLanguageName || "the target language"
                                          } too, so the whole session happens in ${
                                            selectedLanguageName || "the target language"
                                          }.`}
                                          aria-label={`Conversation mode. Instructions change to ${
                                            selectedLanguageName || "the target language"
                                          }.`}
                                          aria-pressed={
                                            !isPodcastDiscussionActive && isConversationLearningMode
                                          }
                                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                            event.stopPropagation();
                                            handleLearningModeChange("conversation");
                                          }}
                                          className={`relative min-h-12 rounded-lg px-2.5 py-1.5 text-center transition-colors ${
                                            isConversationLearningMode &&
                                            !isEffectiveJourneyLearningMode
                                              ? "bg-white text-black"
                                              : "text-zinc-400 hover:bg-white/10 hover:text-white"
                                          }`}
                                        >
                                          <span className="block text-xs font-bold leading-tight">
                                            Conversation
                                          </span>
                                          <span
                                            className={`block text-[10px] font-medium leading-tight mt-0.5 ${
                                              isConversationLearningMode &&
                                              !isEffectiveJourneyLearningMode
                                                ? "text-zinc-600"
                                                : "text-zinc-400"
                                            }`}
                                          >
                                            {`Full ${selectedLanguageName || "target"} immersion`}
                                          </span>
                                        </button>

                                        <button
                                          type="button"
                                          title={`Language Journey: follow a structured ${
                                            selectedLanguageName || "target language"
                                          } chapter with English explanations.`}
                                          aria-label="Language Journey mode. Pick a chapter."
                                          aria-pressed={isEffectiveJourneyLearningMode}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleLearningModeChange("journey");
                                          }}
                                          disabled={!selectedJourney}
                                          className={`relative min-h-12 rounded-lg px-2.5 py-1.5 text-center transition-colors ${
                                            isEffectiveJourneyLearningMode
                                              ? "bg-white text-black"
                                              : selectedJourney
                                                ? "text-zinc-400 hover:bg-white/10 hover:text-white"
                                                : "cursor-not-allowed text-zinc-700"
                                          }`}
                                        >
                                          <span className="block text-xs font-bold leading-tight">
                                            Journey
                                          </span>
                                          <span
                                            className={`block text-[10px] font-medium leading-tight mt-0.5 ${
                                              isEffectiveJourneyLearningMode
                                                ? "text-zinc-600"
                                                : selectedJourney
                                                  ? "text-zinc-400"
                                                  : "text-zinc-600"
                                            }`}
                                          >
                                            Structured chapters
                                          </span>
                                        </button>
                                      </div>

                                      {!isEffectiveJourneyLearningMode && (
                                        <PracticeLevelSelector
                                          value={selectedLanguageLevel}
                                          onValueChange={handleReadyLevelSelection}
                                          variant={brandVariant}
                                        />
                                      )}

                                      <motion.button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void handleStartConversation();
                                        }}
                                        disabled={
                                          isStartSpeakingPending ||
                                          (isEffectiveJourneyLearningMode &&
                                            !selectedJourneyChapterUnlocked)
                                        }
                                        aria-label={
                                          isStartSpeakingPending
                                            ? `Preparing ${selectedLanguageName || "your"} practice`
                                            : isEffectiveJourneyLearningMode
                                              ? undefined
                                              : selectedLanguageLevelStartLabel
                                        }
                                        className={`group relative mt-2 min-h-[4.5rem] w-full overflow-hidden rounded-lg px-8 py-5 transition-colors sm:mt-3 sm:min-h-20 sm:px-10 ${accentPrimaryButtonClass} ${accentPrimaryButtonHoverClass}`}
                                        whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                                        whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
                                      >
                                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                                        <div
                                          className={`relative flex items-center justify-center gap-3 text-lg font-black sm:text-xl lg:text-2xl ${
                                            isSchool ? "text-white" : "text-[#171716]"
                                          }`}
                                        >
                                          {isStartSpeakingPending ? (
                                            <motion.div
                                              animate={
                                                shouldReduceMotion ? undefined : { rotate: 360 }
                                              }
                                              transition={{
                                                duration: 0.9,
                                                repeat: Infinity,
                                                ease: "linear",
                                              }}
                                              className="flex items-center justify-center"
                                            >
                                              <Mic
                                                size={24}
                                                className="drop-shadow-[0_0_10px_rgba(255,255,255,0.35)]"
                                              />
                                            </motion.div>
                                          ) : (
                                            <Mic size={24} />
                                          )}
                                          <span className="min-w-0 text-center leading-tight">
                                            {isStartSpeakingPending
                                              ? `Preparing ${selectedLanguageName || "your"} practice...`
                                              : isEffectiveJourneyLearningMode
                                                ? isSpanishJourney &&
                                                  spanishJourneyPlan?.status === "active"
                                                  ? spanishJourneyRecommendation
                                                    ? "Start next plan session"
                                                    : "Review selected chapter"
                                                  : "Start Chapter"
                                                : PRACTICE_START_LABEL}
                                          </span>
                                        </div>
                                      </motion.button>

                                      {isAuthenticated && (
                                        <ConversationStartChoiceSelector
                                          value={conversationStartChoice}
                                          onChange={setConversationStartChoice}
                                          onUpgrade={() => setIsResumeUpgradeOpen(true)}
                                          subscribed={subscribed}
                                          variant={brandVariant}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </motion.div>

                                {/* Secondary practice paths */}
                                <div
                                  data-chicky-surface
                                  className="mt-4 w-full rounded-lg border border-white/10 bg-black/25 p-4"
                                >
                                  <button
                                    type="button"
                                    onClick={() => setShowSecondaryPractice((prev) => !prev)}
                                    aria-expanded={showSecondaryPractice}
                                    aria-controls="secondary-practice-cards"
                                    className="group flex w-full items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/50 rounded"
                                  >
                                    <div className="text-left">
                                      <h3 className="text-sm font-bold text-white transition-colors group-hover:text-orange-300">
                                        Other ways to practice
                                      </h3>
                                      <p className="mt-0.5 text-xs text-zinc-500">
                                        Choose a focused exercise or practise without a tutor.
                                      </p>
                                    </div>
                                    <ChevronDown
                                      size={18}
                                      className={`shrink-0 text-zinc-400 transition-transform duration-200 group-hover:text-white ${
                                        showSecondaryPractice ? "rotate-180" : ""
                                      }`}
                                      aria-hidden="true"
                                    />
                                  </button>

                                  <AnimatePresence>
                                    {showSecondaryPractice && (
                                      <motion.div
                                        id="secondary-practice-cards"
                                        data-chicky-obstacle
                                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                        animate={{ opacity: 1, height: "auto", marginTop: 16 }}
                                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className={`grid grid-cols-1 gap-3 overflow-hidden ${
                                          showImpromptuEntry ? "md:grid-cols-3" : "md:grid-cols-2"
                                        }`}
                                      >
                                        {/* Visual Learning Card */}
                                        <motion.button
                                          type="button"
                                          whileTap={{ scale: 0.98 }}
                                          transition={{
                                            type: "spring",
                                            stiffness: 400,
                                            damping: 25,
                                          }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (!selectedLanguageCode && !hasExceededGuestLimit) {
                                              setAnimateLanguageShake(true);
                                              setIsLanguageDropdownOpen(true);
                                              setTimeout(() => setAnimateLanguageShake(false), 500);
                                              return;
                                            }

                                            // Show canvas directly without requiring voice agent
                                            setShowCanvas(true);
                                            setCanvasExerciseContent(""); // Empty content to trigger exercise generation
                                            setCanvasExerciseOptions([]);
                                            logger.info(
                                              "Visual exercises started - Canvas shown without voice agent"
                                            );
                                          }}
                                          aria-label="Start visual practice"
                                          className={`group relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 ${
                                            isSchool
                                              ? "hover:border-school-blue/35"
                                              : "hover:border-orange-500/30"
                                          }`}
                                        >
                                          <div className="absolute right-4 top-4 opacity-15">
                                            <Target
                                              size={30}
                                              className={
                                                isSchool ? "text-school-blue" : "text-orange-500"
                                              }
                                            />
                                          </div>

                                          <div className="relative z-10 flex h-full flex-col">
                                            <div
                                              className={`mb-3 inline-flex self-start items-center gap-2 rounded-md border px-2 py-1 ${
                                                isSchool
                                                  ? "bg-school-blue/10 border-school-blue/20"
                                                  : "bg-orange-500/10 border-orange-500/20"
                                              }`}
                                            >
                                              <span
                                                className={`text-xs font-bold ${
                                                  isSchool ? "text-school-focus" : "text-orange-400"
                                                }`}
                                              >
                                                Easy Start
                                              </span>
                                            </div>

                                            <h4 className="mb-1 flex items-center gap-3 text-base font-bold text-white">
                                              Visual practice
                                            </h4>
                                            <p className="mb-4 pr-8 text-sm leading-5 text-zinc-500">
                                              Quick fill-in-the-gap exercises for vocabulary recall.
                                            </p>

                                            <span
                                              className={`mt-auto flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 ${
                                                isSchool
                                                  ? "group-hover:border-school-blue/35"
                                                  : "group-hover:border-orange-500/30"
                                              }`}
                                            >
                                              Play now
                                              <ChevronDown className="rotate-[-90deg]" size={16} />
                                            </span>
                                          </div>
                                        </motion.button>

                                        {/* Writing Practice Card */}
                                        <motion.button
                                          type="button"
                                          whileTap={{ scale: 0.98 }}
                                          transition={{
                                            type: "spring",
                                            stiffness: 400,
                                            damping: 25,
                                          }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (startWritingPractice)
                                              startWritingPractice(fromLanguageName);
                                          }}
                                          aria-label="Start writing practice"
                                          className={`group relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 ${
                                            isSchool
                                              ? "hover:border-school-blue/35"
                                              : "hover:border-orange-500/30"
                                          }`}
                                        >
                                          <div className="absolute right-4 top-4 opacity-15">
                                            <PenLine
                                              size={30}
                                              className={
                                                isSchool ? "text-school-blue" : "text-orange-500"
                                              }
                                            />
                                          </div>

                                          <div className="relative z-10 flex h-full flex-col">
                                            <div
                                              className={`mb-3 inline-flex self-start items-center gap-2 rounded-md border px-2 py-1 ${
                                                isSchool
                                                  ? "border-school-blue/20 bg-school-blue/10"
                                                  : "border-orange-500/20 bg-orange-500/10"
                                              }`}
                                            >
                                              <span
                                                className={`text-xs font-bold ${
                                                  isSchool ? "text-school-focus" : "text-orange-400"
                                                }`}
                                              >
                                                Grammar Focus
                                              </span>
                                            </div>

                                            <h4 className="mb-1 flex items-center gap-3 text-base font-bold text-white">
                                              Writing practice
                                            </h4>
                                            <p className="mb-4 pr-8 text-sm leading-5 text-zinc-500">
                                              Write at your pace and get focused grammar feedback.
                                            </p>

                                            <span
                                              className={`mt-auto flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 ${
                                                isSchool
                                                  ? "group-hover:border-school-blue/35"
                                                  : "group-hover:border-orange-500/30"
                                              }`}
                                            >
                                              Start writing
                                              <ChevronDown className="rotate-[-90deg]" size={16} />
                                            </span>
                                          </div>
                                        </motion.button>

                                        {showImpromptuEntry && (
                                          <motion.button
                                            type="button"
                                            title="Answer one prompt without tutor interruptions, then get a focused report."
                                            aria-label="Try Impromptu speaking practice. Record one answer and get feedback."
                                            whileTap={{ scale: 0.98 }}
                                            transition={{
                                              type: "spring",
                                              stiffness: 400,
                                              damping: 25,
                                            }}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
                                                posthog.capture("impromptu_mode_selected", {
                                                  surface: "fullscreen_selector",
                                                  language_code: selectedLanguageCode,
                                                  learner_profile: selectedLanguageLevel,
                                                });
                                              }
                                              setIsImpromptuOpen(true);
                                            }}
                                            className={`group relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 ${
                                              isSchool
                                                ? "hover:border-school-blue/35"
                                                : "hover:border-orange-500/30"
                                            }`}
                                          >
                                            <div className="absolute right-4 top-4 opacity-15">
                                              <Mic
                                                size={30}
                                                className={
                                                  isSchool ? "text-school-blue" : "text-orange-500"
                                                }
                                              />
                                            </div>

                                            <div className="relative z-10 flex h-full flex-col">
                                              <div
                                                className={`mb-3 inline-flex self-start items-center gap-2 rounded-md border px-2 py-1 ${
                                                  isSchool
                                                    ? "border-school-blue/20 bg-school-blue/10"
                                                    : "border-orange-500/20 bg-orange-500/10"
                                                }`}
                                              >
                                                <span
                                                  className={`text-xs font-bold ${
                                                    isSchool
                                                      ? "text-school-focus"
                                                      : "text-orange-400"
                                                  }`}
                                                >
                                                  Solo speaking
                                                </span>
                                              </div>

                                              <h4 className="mb-1 flex items-center gap-3 text-base font-bold text-white">
                                                Impromptu practice
                                              </h4>
                                              <p className="mb-4 pr-8 text-sm leading-5 text-zinc-500">
                                                Answer one prompt and get feedback on your delivery
                                                and language.
                                              </p>

                                              <span
                                                className={`mt-auto flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 ${
                                                  isSchool
                                                    ? "group-hover:border-school-blue/35"
                                                    : "group-hover:border-orange-500/30"
                                                }`}
                                              >
                                                Try a prompt
                                                <ChevronDown
                                                  className="rotate-[-90deg]"
                                                  size={16}
                                                />
                                              </span>
                                            </div>
                                          </motion.button>
                                        )}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  onSessionModeChange ? onSessionModeChange(null) : null
                                }
                                className="text-gray-400 hover:text-white px-3 py-2 text-sm"
                              >
                                Back
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <RoomContext.Provider value={room}>
                      {isRecording && isConnected && (
                        <TrackedRoomAudioRenderer
                          activeSession={isRecording && isConnected}
                          roomName={room.name || null}
                          surface="listen-view-transcript"
                        />
                      )}
                      <div className="relative flex flex-col h-full w-full items-center justify-start pt-4">
                        {shouldBootstrapAvatar && (
                          <div className="pointer-events-none absolute left-1/2 top-4 z-0 -translate-x-1/2 opacity-0">
                            <VoiceAgent
                              room={room}
                              isRecording={isRecording}
                              renderAudioRenderer={false}
                            />
                          </div>
                        )}
                        {shouldRenderSessionTranscript ? (
                          <div className="flex-1 w-full flex flex-col items-center justify-start overflow-hidden pb-6 pt-2 min-h-0">
                            <TranscriptionView
                              onClose={disconnectFromLiveKit}
                              connectionState={connectionState}
                              connectionError={connectionError}
                              retryConnection={retryConnection}
                              audioTrackError={audioTrackError}
                              audioRetryCount={audioRetryCount}
                              retryAudioTrack={retryAudioTrack}
                              clearAudioErrors={clearAudioErrors}
                              sessionDuration={recordingTime}
                              disableAvatar={shouldDisableTranscriptAvatar}
                              selectedLanguageCode={selectedLanguageCode}
                              selectedLanguageLevel={selectedLanguageLevel}
                              isActiveSession={isRecording && isConnected}
                              showHeaderWhenInactive={false}
                              isMuted={isMuted}
                              onToggleMute={toggleMute}
                              setMicrophoneMuted={setMicrophoneMuted}
                              enableVoiceNotes={voiceNoteTurnsEnabled}
                              onConversationActivity={onConversationActivity}
                              renderRoomAudio={false}
                              tutorWhiteboardEnabled={tutorWhiteboardEnabled}
                              sessionMode={sessionMode || undefined}
                              learningMode={effectiveActiveSessionLearningMode}
                              onLearningModeChange={handleActiveLearningModeChange}
                              defaultTranslationLanguageCode={guidedInstructionLanguageCode}
                              instructionLanguageCode={nativeLanguageCode}
                              translationLanguageCode={translationLanguageCode}
                              onTranslationLanguageChange={onTranslationLanguageChange}
                              translationsEnabled={transcriptTranslationsEnabled}
                              onTranslationsEnabledChange={setTranscriptTranslationsEnabled}
                              continuationConversation={continuationConversation}
                              previousTutorMessages={previousTutorMessages}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center flex-1 px-4">
                            {connectionState === "failed" ? (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                                data-chicky-obstacle
                                className="flex w-full max-w-md flex-col items-center justify-center text-center"
                              >
                                <div className="relative w-full overflow-hidden rounded-lg border border-red-500/20 bg-[#0e0e12] p-7 md:p-9">
                                  <div className="relative z-10 flex flex-col items-center">
                                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg border border-red-500/25 bg-red-500/10">
                                      <AlertTriangle size={22} className="text-red-400" />
                                    </div>

                                    <h3 className="text-xl font-bold text-white mb-2">
                                      We couldn't start your session
                                    </h3>
                                    <p className="mb-7 max-w-sm text-sm leading-relaxed text-zinc-400">
                                      {getUserFacingConnectionFailureMessage(connectionError)}
                                    </p>

                                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
                                      {retryConnection && (
                                        <motion.button
                                          whileHover={{ scale: 1.03 }}
                                          whileTap={{ scale: 0.97 }}
                                          onClick={retryConnection}
                                          className={`inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white transition-colors sm:flex-1 ${
                                            isSchool
                                              ? "bg-school-blue hover:bg-school-blue-hover"
                                              : "bg-orange-500 hover:bg-orange-600"
                                          }`}
                                        >
                                          <RotateCw size={16} /> Retry
                                        </motion.button>
                                      )}
                                      <motion.button
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => {
                                          // Reset state and go back to dashboard
                                          disconnectFromLiveKit(
                                            false,
                                            false,
                                            "listen_error_go_back"
                                          );
                                          if (onSessionModeChange) onSessionModeChange(null);
                                        }}
                                        className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-zinc-700/50 bg-zinc-800/80 px-6 py-3 font-semibold text-zinc-300 transition-colors hover:border-zinc-600/50 hover:bg-zinc-700/80 hover:text-white sm:flex-1"
                                      >
                                        Back to setup
                                      </motion.button>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </RoomContext.Provider>
                  )}
                </div>

                {preSessionVariant !== "configurable-start" && shellLayout !== "inline" && (
                  <div className="p-4">
                    {!isRecording && (!segments || segments.length === 0) && (
                      <div className="h-[60px] md:h-[80px]"></div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <WhiteboardSessionRecap
        sessionActive={shouldRenderSessionTranscript}
        targetLanguage={selectedLanguageName || ""}
        instructionLanguage={fromLanguageName || "English"}
        languageLevel={selectedLanguageLevel}
      />
    </>
  );
};

export default ListenView;
